<script setup lang="ts">
import {
  Navigation,
  Locate,
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  Layers,
  Map as MapIcon,
  MapPinned,
  Eye,
  EyeOff,
} from '@lucide/vue'
import maplibregl, {
  AJAXError,
  type IControl,
  type LngLatBoundsLike,
  type MapOptions,
  type MapMouseEvent,
} from 'maplibre-gl'
import { ref, onMounted, onUnmounted, provide, shallowRef, watch, h, render, computed } from 'vue'

import 'maplibre-gl/dist/maplibre-gl.css'

import MapStyleTiles from '@/components/map/MapStyleTiles.vue'
import MapToolSheet from '@/components/map/MapToolSheet.vue'
import { useEventListener } from '@/composables/useEventListener'
import {
  DEFAULT_MAP_STYLE_PREFERENCE,
  MAP_STYLE_OPTIONS,
  getOfflineStyleCacheKey,
  mapStylePreferenceFromOfflineStyleCacheKey,
  normalizeMapStylePreference,
  parseCustomMapStyleJson,
  resolveRuntimeMapStyle,
  type BuiltInMapStyleId,
  type MapStylePreference,
} from '@/config/mapStyle'
import { E2E_MAP_STYLE, isE2eMode } from '@/e2e/e2eMode'
import { connectivityService } from '@/services/connectivityService'
import { deviceHeadingService } from '@/services/deviceHeadingService'
import {
  clearWatch,
  getGeolocationPermissionState,
  getCurrentPosition,
  isGeolocationSupported,
  watchPosition,
  type GeoError,
  type GeoPermissionState,
  type GeoPosition,
  type GeoWatchHandle,
} from '@/services/geolocationService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  optimizeFitBoundsCameraOptions,
  optimizeFlyToCameraOptions,
  restoreExpensiveTerrainLayers,
} from '@/services/mapCameraOptimizationService'
import { nativeTileProxyService } from '@/services/nativeTileProxyService'
import { applyOfflineRewrite } from '@/services/offlineMapProtocol'
import { isTauri, isTauriAndroidMobile, isTauriMobile } from '@/services/platformService'
import {
  applyRoutecampContours,
  areRoutecampBasemapSourcesLoaded,
  holdRoutecampContourTiles,
  setRoutecampContourOfflineContext,
  HILLSHADE_SOURCE_ID,
} from '@/services/routecampContours'
import { toNativeTileUrl } from '@/services/routecampTilesUrl'
import { readSafeAreaInsetBottomPx } from '@/services/safeAreaService'
import { showError } from '@/services/toastService'
import { isSmallScreen, isTouchDevice } from '@/services/uiService'
import {
  uiStatePersistenceService,
  type MapViewportSnapshot,
} from '@/services/uiStatePersistenceService'
import { useMapStore } from '@/stores/mapStore'
import { useOfflineStore, type OfflineEntry } from '@/stores/offlineStore'
import { useRecordingStore } from '@/stores/recordingStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { createMapMarker } from '@/utils/mapMarkerElement'

import type { Component } from 'vue'

// WebGL detection utility
const isWebGLSupported = (): boolean => {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    return !!(gl && gl instanceof WebGLRenderingContext)
  } catch {
    return false
  }
}

const MAP_CENTER: [number, number] = [11.0, 48.0]
const MAP_ZOOM = 9
// Constrain zoom so the user cannot zoom out past roughly the whole globe nor
// zoom in beyond street/building detail (avoids the "centimeter scale" zoom).
const MAP_MIN_ZOOM = 2
const MAP_MAX_ZOOM = 18
const MAP_CONTROL_ZOOM_DURATION_MS = 320
const MAP_FIT_BOUNDS_DURATION_MS = 650
const MAP_WHEEL_ZOOM_RATE = 1 / 650
const MAP_TRACKPAD_ZOOM_RATE = 1 / 130
const PINCH_CLICK_SUPPRESSION_MS = 550
const TAURI_WINDOW_RESUMED_EVENT = 'tauri://resumed'
const FOLLOW_LOCATION_FOREGROUND_COALESCE_MS = 1000
const MOBILE_ROTATION_DEADBAND_PX = 50
const MOBILE_BOTTOM_EDGE_PAN_GUARD_PX = 24
const TERRAIN_ENABLE_PITCH = 25
const TERRAIN_DISABLE_PITCH = 15
const TERRAIN_ENABLE_ZOOM = 11
const TERRAIN_DISABLE_ZOOM = 10
const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 15000,
}

interface TerrainSpecification {
  source: string
  exaggeration?: number
}

interface TouchRotateThresholdHandler {
  _minDiameter?: number
  _startVector?: {
    angleWith(other: unknown): number
  }
  _isBelowThreshold?: (vector: { mag(): number; angleWith(other: unknown): number }) => boolean
}

interface TouchZoomRotateWithInternals {
  _touchRotate?: TouchRotateThresholdHandler
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

const configureMapInteractionSmoothing = (map: maplibregl.Map) => {
  map.scrollZoom.setWheelZoomRate(MAP_WHEEL_ZOOM_RATE)
  map.scrollZoom.setZoomRate(MAP_TRACKPAD_ZOOM_RATE)
}

const setFollowLocationZoomAnchor = (map: maplibregl.Map, active: boolean) => {
  if (active) {
    map.scrollZoom.enable({ around: 'center' })
    map.touchZoomRotate.enable({ around: 'center' })
    return
  }

  map.scrollZoom.enable()
  map.touchZoomRotate.enable()
}

const increaseMobileRotationDeadband = (map: maplibregl.Map) => {
  const touchZoomRotate = map.touchZoomRotate as typeof map.touchZoomRotate &
    TouchZoomRotateWithInternals
  const touchRotate = touchZoomRotate._touchRotate
  if (!touchRotate) {
    return
  }

  touchRotate._isBelowThreshold = (vector) => {
    if (!touchRotate._startVector) {
      return true
    }

    touchRotate._minDiameter = Math.min(touchRotate._minDiameter ?? vector.mag(), vector.mag())
    const circumference = Math.PI * touchRotate._minDiameter
    const threshold = (MOBILE_ROTATION_DEADBAND_PX / circumference) * 360
    const bearingDeltaSinceStart = touchRotate._startVector.angleWith(vector) * (180 / Math.PI)
    return Math.abs(bearingDeltaSinceStart) < threshold
  }
}

const installPinchClickGuard = (container: HTMLElement) => {
  let pinchInProgress = false
  let lastPinchAt = 0

  const markPinch = () => {
    pinchInProgress = true
    lastPinchAt = Date.now()
  }

  const handleTouchStart = (event: TouchEvent) => {
    if (event.touches.length > 1) markPinch()
  }

  const handleTouchMove = (event: TouchEvent) => {
    if (event.touches.length > 1) markPinch()
  }

  const handleTouchEnd = (event: TouchEvent) => {
    if (pinchInProgress) lastPinchAt = Date.now()
    if (event.touches.length === 0) pinchInProgress = false
  }

  const handleClick = (event: MouseEvent) => {
    if (Date.now() - lastPinchAt > PINCH_CLICK_SUPPRESSION_MS) return

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  container.addEventListener('touchstart', handleTouchStart, true)
  container.addEventListener('touchmove', handleTouchMove, true)
  container.addEventListener('touchend', handleTouchEnd, true)
  container.addEventListener('touchcancel', handleTouchEnd, true)
  container.addEventListener('click', handleClick, true)

  return () => {
    container.removeEventListener('touchstart', handleTouchStart, true)
    container.removeEventListener('touchmove', handleTouchMove, true)
    container.removeEventListener('touchend', handleTouchEnd, true)
    container.removeEventListener('touchcancel', handleTouchEnd, true)
    container.removeEventListener('click', handleClick, true)
  }
}

const installBottomEdgePanGuard = (container: HTMLElement, map: maplibregl.Map) => {
  let guardedTouchActive = false
  let dragPanWasEnabled = false

  const releaseGuard = () => {
    if (!guardedTouchActive) {
      return
    }

    guardedTouchActive = false
    if (dragPanWasEnabled) {
      map.dragPan.enable()
    }
    dragPanWasEnabled = false
  }

  const shouldGuardTouchStart = (event: TouchEvent) => {
    if (event.touches.length !== 1) {
      return false
    }

    const touch = event.touches[0]
    if (!touch) {
      return false
    }

    const bottomInset = readSafeAreaInsetBottomPx()
    const edgeStripHeight = bottomInset + MOBILE_BOTTOM_EDGE_PAN_GUARD_PX
    return touch.clientY >= window.innerHeight - edgeStripHeight
  }

  const handleTouchStart = (event: TouchEvent) => {
    if (guardedTouchActive || !shouldGuardTouchStart(event)) {
      return
    }

    dragPanWasEnabled = typeof map.dragPan.isEnabled === 'function' ? map.dragPan.isEnabled() : true
    if (dragPanWasEnabled) {
      map.dragPan.disable()
    }
    guardedTouchActive = true
  }

  const handleTouchEnd = (event: TouchEvent) => {
    if (event.touches.length === 0) {
      releaseGuard()
    }
  }

  const handleTouchCancel = () => {
    releaseGuard()
  }

  container.addEventListener('touchstart', handleTouchStart, true)
  container.addEventListener('touchend', handleTouchEnd, true)
  container.addEventListener('touchcancel', handleTouchCancel, true)

  return () => {
    releaseGuard()
    container.removeEventListener('touchstart', handleTouchStart, true)
    container.removeEventListener('touchend', handleTouchEnd, true)
    container.removeEventListener('touchcancel', handleTouchCancel, true)
  }
}

interface SessionCustomStyleEntry {
  id: number
  name: string
  customStyleUrl: string | null
  customStyleJson: string | null
  fileName: string | null
}

interface MapStyleCard {
  id: string
  label: string
  description: string
  cardType: 'builtin' | 'session'
  builtInStyleId?: BuiltInMapStyleId
  sessionStyleId?: number
}

const sessionCustomStylesStore: SessionCustomStyleEntry[] = []
let nextSessionCustomStyleId = 1

// The developer-oriented "Map layers" control. Always on for now (pre-production — we're still
// iterating on UX/UI/backend and need the raw layer toggles). Flip to `import.meta.env.DEV` to make
// it developer-only before launch (the user-facing POI controls live in the "Map content" panel).
const MAP_LAYERS_CONTROL_ENABLED = true

const props = withDefaults(
  defineProps<{
    initialBounds?: LngLatBoundsLike | null
    selectionMode?: boolean
  }>(),
  { initialBounds: null, selectionMode: false },
)

// Helper function to render Vue component to HTML element
function renderIcon(icon: Component, props = { size: 20 }): HTMLElement {
  const container = document.createElement('div')
  const vNode = h(icon, props)
  render(vNode, container)
  return container
}

type UserLocationMarkerVariant = 'passive' | 'tracking'

const normalizeLocationHeading = (heading: number | null | undefined): number | null => {
  if (typeof heading !== 'number' || !Number.isFinite(heading) || heading < 0 || heading > 360) {
    return null
  }
  return heading === 360 ? 0 : heading
}

const createUserLocationMarkerElement = (variant: UserLocationMarkerVariant): HTMLDivElement => {
  const marker = document.createElement('div')
  marker.className =
    variant === 'passive' ? 'passive-user-location-marker' : 'custom-user-location-marker'

  const heading = document.createElement('div')
  heading.className = 'location-marker-heading'
  marker.appendChild(heading)

  const dot = document.createElement('div')
  dot.className = variant === 'passive' ? 'passive-location-marker-inner' : 'location-marker-inner'
  marker.appendChild(dot)

  if (variant === 'tracking') {
    const pulse = document.createElement('div')
    pulse.className = 'location-marker-pulse'
    marker.appendChild(pulse)
  }

  return marker
}

const updateUserLocationMarkerHeading = (
  marker: maplibregl.Marker,
  heading: number | null | undefined,
): void => {
  const normalizedHeading = normalizeLocationHeading(heading)
  marker.getElement().classList.toggle('has-heading', normalizedHeading !== null)
  marker.setRotation(normalizedHeading ?? 0)
}

interface MapStyleControlHost {
  setMapStyleActive(active: boolean): void
  setDeviceHeading(heading: number | null): void
  focusCurrentLocation(options?: { preserveZoom?: boolean; allowStart?: boolean }): Promise<void>
  isFollowLocationActive(): boolean
  stopFollowLocation(): Promise<void>
  stopFollowLocationIfPannedAway(deadbandPx: number): Promise<boolean>
}

type CurrentLocationSource = 'passive' | 'tracking'
type CurrentLocationUpdate = {
  lat: number
  lon: number
  accuracyM: number | null
  timestamp: number
  source: CurrentLocationSource
}

const FOLLOW_LOCATION_PAN_DEADBAND_PX = 40

// Mobile-specific control implementation for locate and compass only
class MobileMapControls implements IControl {
  private container: HTMLElement
  private map?: maplibregl.Map
  private tracking: boolean = false
  private trackingGeneration = 0
  private locationRequestGeneration: number | null = null
  private watchHandle: GeoWatchHandle | null = null
  private userLocationMarker: maplibregl.Marker | null = null
  private deviceHeading: number | null = null
  private lastGpsHeading: number | null = null
  private lastTrackedLngLat: { lng: number; lat: number } | null = null
  private accuracyRadius: number = 0
  private rotationRafId: number | null = null
  private compassIcon: HTMLElement
  private mapStyleBtn: HTMLButtonElement
  private immersiveBtn: HTMLButtonElement
  private hideBtn: HTMLButtonElement
  private mapContentWrapper?: HTMLElement

  constructor(
    private onMapStyleToggle: () => void,
    private onImmersiveToggle: () => void,
    private onHideToggle: () => void,
  ) {
    // Create the main container
    this.container = document.createElement('div')
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group mobile-map-controls'

    // Create the controls container that will hold controls vertically
    const controlsContainer = document.createElement('div')
    controlsContainer.className = 'mobile-controls-container'

    // 1. Add locate control
    const locateControl = this.createLocateControl()
    controlsContainer.appendChild(locateControl)

    // 2. Add compass control
    const compassControl = this.createCompassControl()
    this.compassIcon = compassControl.querySelector('.compass-icon-wrapper') as HTMLElement
    controlsContainer.appendChild(compassControl)

    // 3. Add "Map content" control (user-facing POI categories to show + add to a collection)
    const mapContentControl = this.createMapContentControl()
    this.mapContentWrapper = mapContentControl
    controlsContainer.appendChild(mapContentControl)

    // 3b. Add layers toggle control — a developer tool (always on for now; see MAP_LAYERS_CONTROL_ENABLED).
    if (MAP_LAYERS_CONTROL_ENABLED) {
      const layersControl = this.createLayersControl()
      controlsContainer.appendChild(layersControl)
    }

    // 4. Add map style control
    const mapStyleControl = this.createMapStyleControl()
    this.mapStyleBtn = mapStyleControl.querySelector(
      '.maplibregl-ctrl-map-style',
    ) as HTMLButtonElement
    controlsContainer.appendChild(mapStyleControl)

    // 5. Add show/hide map data (tracks, waypoints, POIs, photos) toggle — kept second from the
    // bottom (just above the full-map toggle) so the eye is easy to reach.
    const hideControl = this.createHideElementsControl()
    this.hideBtn = hideControl.querySelector('.maplibregl-ctrl-hide-elements') as HTMLButtonElement
    controlsContainer.appendChild(hideControl)

    // 6. Add "Full map" immersive-mode toggle
    const immersiveControl = this.createImmersiveControl()
    this.immersiveBtn = immersiveControl.querySelector(
      '.maplibregl-ctrl-immersive',
    ) as HTMLButtonElement
    controlsContainer.appendChild(immersiveControl)

    // Add the controls container to the main container
    this.container.appendChild(controlsContainer)
  }

  onAdd(map: maplibregl.Map): HTMLElement {
    this.map = map

    // Set up event listeners for compass rotation
    this.setupCompassEvents(map)

    return this.container
  }

  onRemove(): void {
    // Clean up event listeners
    if (this.map) {
      this.map.off('rotate', this.updateCompassRotation.bind(this))
      this.map.off('pitch', this.updateCompassRotation.bind(this))
      this.map.off('moveend', this.updateCompassRotation.bind(this))
    }

    // Stop location tracking if active (teardown: keep persisted follow mode)
    void this.stopTracking({ persistFollowState: false })

    // Clean up rotation frame
    if (this.rotationRafId !== null) {
      cancelAnimationFrame(this.rotationRafId)
      this.rotationRafId = null
    }

    this.container.parentNode?.removeChild(this.container)
    this.map = undefined
  }

  // Create the compass control
  private createCompassControl(): HTMLElement {
    const compassWrapper = document.createElement('div')
    compassWrapper.className = 'map-ctrl-btn compass-wrapper'

    const compassBtn = document.createElement('button')
    compassBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-compass'
    compassBtn.setAttribute('aria-label', 'Reset Bearing to North')

    const compassIconWrapper = document.createElement('div')
    compassIconWrapper.className = 'compass-icon-wrapper'
    compassIconWrapper.appendChild(renderIcon(Navigation, { size: 20 }))

    compassBtn.appendChild(compassIconWrapper)

    compassBtn.addEventListener('click', () => {
      if (this.map && this.map.getBearing() !== 0) {
        this.map.easeTo({
          bearing: 0,
          pitch: 0,
          duration: 500,
        })
      }
    })

    compassWrapper.appendChild(compassBtn)
    return compassWrapper
  }

  // Set up compass rotation events
  private setupCompassEvents(map: maplibregl.Map): void {
    const rotationHandler = this.updateCompassRotation.bind(this)
    map.on('rotate', rotationHandler)
    map.on('pitch', rotationHandler)
    map.on('moveend', rotationHandler)

    // Initial rotation update - wait for map to be fully loaded
    map.on('load', () => {
      this.updateCompassRotation()
    })

    // Also update immediately if map is already loaded
    if (map.loaded()) {
      this.updateCompassRotation()
    }
  }

  // Update the compass rotation based on map bearing
  private updateCompassRotation(): void {
    if (this.rotationRafId !== null) {
      cancelAnimationFrame(this.rotationRafId)
    }

    this.rotationRafId = requestAnimationFrame(() => {
      if (!this.map) return

      const bearing = this.map.getBearing()
      // Apply both the base rotation (-45deg) and the bearing rotation
      this.compassIcon.style.transform = `rotate(${-45 - bearing}deg)`

      const compassBtn = this.compassIcon.closest('.maplibregl-ctrl-compass') as HTMLElement
      if (bearing === 0) {
        compassBtn.classList.remove('is-active')
      } else {
        compassBtn.classList.add('is-active')
      }
    })
  }

  // Create the locate control
  private createLocateControl(): HTMLElement {
    const locateWrapper = document.createElement('div')
    locateWrapper.className = 'map-ctrl-btn locate-wrapper'

    const locateBtn = document.createElement('button')
    locateBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-geolocate'
    locateBtn.setAttribute('aria-label', 'Find my location')
    locateBtn.appendChild(renderIcon(Locate, { size: 20 }))

    locateBtn.addEventListener('click', () => {
      void deviceHeadingService.requestPermission()
      void this.toggleGeolocation()
    })

    locateWrapper.appendChild(locateBtn)
    return locateWrapper
  }

  // Create the "Map content" toggle control (POI categories) for mobile
  private createMapContentControl(): HTMLElement {
    const wrapper = document.createElement('div')
    wrapper.className = 'map-ctrl-btn map-content-wrapper'

    const btn = document.createElement('button')
    btn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-map-content'
    btn.setAttribute('aria-label', 'Toggle Map content')
    btn.appendChild(renderIcon(MapPinned, { size: 20 }))

    btn.addEventListener('click', () => {
      uiStore.toggleMapContentPanel()
    })

    wrapper.appendChild(btn)
    return wrapper
  }

  setMapContentVisible(visible: boolean): void {
    if (this.mapContentWrapper) this.mapContentWrapper.style.display = visible ? '' : 'none'
  }

  // Create the layers toggle control for mobile
  private createLayersControl(): HTMLElement {
    const layersWrapper = document.createElement('div')
    layersWrapper.className = 'map-ctrl-btn layers-wrapper'

    const layersBtn = document.createElement('button')
    layersBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-layers'
    layersBtn.setAttribute('aria-label', 'Toggle Map Layers Panel')
    layersBtn.appendChild(renderIcon(Layers, { size: 20 }))

    layersBtn.addEventListener('click', () => {
      uiStore.toggleLayersPanel()
    })

    layersWrapper.appendChild(layersBtn)
    return layersWrapper
  }

  private createMapStyleControl(): HTMLElement {
    const mapStyleWrapper = document.createElement('div')
    mapStyleWrapper.className = 'map-ctrl-btn map-style-wrapper'

    const mapStyleBtn = document.createElement('button')
    mapStyleBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-map-style'
    mapStyleBtn.setAttribute('aria-label', 'Toggle Map Style Panel')
    mapStyleBtn.appendChild(renderIcon(MapIcon, { size: 20 }))

    mapStyleBtn.addEventListener('click', () => {
      this.onMapStyleToggle()
    })

    mapStyleWrapper.appendChild(mapStyleBtn)
    return mapStyleWrapper
  }

  setMapStyleActive(active: boolean): void {
    if (!this.mapStyleBtn) return
    if (active) {
      this.mapStyleBtn.classList.add('is-active')
    } else {
      this.mapStyleBtn.classList.remove('is-active')
    }
  }

  setDeviceHeading(heading: number | null): void {
    this.deviceHeading = heading
    if (this.userLocationMarker) {
      updateUserLocationMarkerHeading(this.userLocationMarker, heading ?? this.lastGpsHeading)
    }
  }

  private createImmersiveControl(): HTMLElement {
    const immersiveWrapper = document.createElement('div')
    immersiveWrapper.className = 'map-ctrl-btn immersive-wrapper'

    const immersiveBtn = document.createElement('button')
    immersiveBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-immersive'
    immersiveBtn.setAttribute('aria-label', 'Full map')
    immersiveBtn.setAttribute('data-testid', 'map-immersive-toggle')
    immersiveBtn.appendChild(renderIcon(Maximize, { size: 20 }))

    immersiveBtn.addEventListener('click', () => {
      this.onImmersiveToggle()
    })

    immersiveWrapper.appendChild(immersiveBtn)
    return immersiveWrapper
  }

  // Reflect the immersive flag in the toggle itself. The control cluster deliberately keeps the
  // same fixed position so entering full-map mode does not make the controls jump.
  setImmersiveActive(active: boolean): void {
    if (this.immersiveBtn) {
      this.immersiveBtn.replaceChildren(renderIcon(active ? Minimize : Maximize, { size: 20 }))
      this.immersiveBtn.setAttribute('aria-label', active ? 'Exit full map' : 'Full map')
      this.immersiveBtn.classList.toggle('is-active', active)
    }
  }

  private createHideElementsControl(): HTMLElement {
    const hideWrapper = document.createElement('div')
    hideWrapper.className = 'map-ctrl-btn hide-elements-wrapper'

    const hideBtn = document.createElement('button')
    hideBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-hide-elements'
    hideBtn.setAttribute('aria-label', 'Hide tracks')
    hideBtn.setAttribute('data-testid', 'map-hide-elements-toggle')
    hideBtn.appendChild(renderIcon(Eye, { size: 20 }))

    hideBtn.addEventListener('click', () => {
      this.onHideToggle()
    })

    hideWrapper.appendChild(hideBtn)
    return hideWrapper
  }

  // Reflect the show/hide flag: Eye when tracks are visible (tap to hide), EyeOff when hidden.
  setElementsHidden(hidden: boolean): void {
    if (!this.hideBtn) return
    this.hideBtn.replaceChildren(renderIcon(hidden ? EyeOff : Eye, { size: 20 }))
    this.hideBtn.setAttribute('aria-label', hidden ? 'Show tracks' : 'Hide tracks')
    this.hideBtn.classList.toggle('is-active', hidden)
  }

  private async toggleGeolocation(): Promise<void> {
    if (!this.map) return

    // If already tracking, stop tracking
    if (this.tracking) {
      void this.stopTracking()
      return
    }

    // Start tracking user's location
    await this.startTracking()
  }

  // Start tracking user location
  async focusCurrentLocation(
    options: { preserveZoom?: boolean; allowStart?: boolean } = {},
  ): Promise<void> {
    if (!this.map) return
    if (!this.tracking) {
      if (options.allowStart === false) return
      await this.startTracking({ forceCenter: true, preserveZoom: options.preserveZoom })
      return
    }

    const generation = this.trackingGeneration
    if (this.locationRequestGeneration === generation) return
    this.locationRequestGeneration = generation
    try {
      const position = await getCurrentPosition(GEOLOCATION_OPTIONS)
      if (!this.tracking || generation !== this.trackingGeneration) return
      this.updatePosition(position, { forceCenter: true, preserveZoom: options.preserveZoom })
      await this.restartTrackingWatch(generation)
    } catch (error) {
      if (this.tracking && generation === this.trackingGeneration) {
        this.handleGeolocationError(error as GeoError)
      } else {
        logService.debug(LOG_CATEGORIES.MAP, 'Ignoring stale location request error', { error })
      }
    } finally {
      if (this.locationRequestGeneration === generation) {
        this.locationRequestGeneration = null
      }
    }
  }

  isFollowLocationActive(): boolean {
    return this.tracking
  }

  async stopFollowLocation(): Promise<void> {
    await this.stopTracking()
  }

  async stopFollowLocationIfPannedAway(deadbandPx: number): Promise<boolean> {
    if (!this.hasTrackedLocationMovedAwayFromCenter(deadbandPx)) {
      return false
    }

    await this.stopTracking()
    return true
  }

  private async startTracking(
    options: { forceCenter?: boolean; preserveZoom?: boolean } = {},
  ): Promise<void> {
    if (!this.map) {
      return
    }
    if (!isGeolocationSupported()) {
      showError('Geolocation is not supported on this device')
      return
    }

    // Set button to active state
    const locateBtn = this.container.querySelector('.maplibregl-ctrl-geolocate') as HTMLElement
    locateBtn.classList.add('active')
    const generation = ++this.trackingGeneration
    this.locationRequestGeneration = generation
    this.tracking = true
    setFollowLocationZoomAnchor(this.map, true)
    setPassiveLocationMarkerSuppressed(true)
    persistFollowLocationState(true)

    try {
      const position = await getCurrentPosition(GEOLOCATION_OPTIONS)
      if (!this.tracking || generation !== this.trackingGeneration) return
      this.updatePosition(position, {
        forceCenter: options.forceCenter,
        preserveZoom: options.preserveZoom,
      })
      await this.restartTrackingWatch(generation)
    } catch (error) {
      if (this.tracking && generation === this.trackingGeneration) {
        this.handleGeolocationError(error as GeoError)
      } else {
        logService.debug(LOG_CATEGORIES.MAP, 'Ignoring stale location request error', { error })
      }
    } finally {
      if (this.locationRequestGeneration === generation) {
        this.locationRequestGeneration = null
      }
    }
  }

  private async restartTrackingWatch(generation = this.trackingGeneration): Promise<void> {
    if (this.watchHandle) {
      const previousHandle = this.watchHandle
      this.watchHandle = null
      try {
        await clearWatch(previousHandle)
      } catch (error) {
        logService.warn(LOG_CATEGORIES.MAP, 'Failed to clear existing location watch', { error })
      }
    }

    if (!this.tracking || generation !== this.trackingGeneration) return

    try {
      const nextHandle = await watchPosition(
        GEOLOCATION_OPTIONS,
        (position) => {
          if (this.tracking && generation === this.trackingGeneration) {
            this.updatePosition(position)
          }
        },
        (error) => {
          if (this.tracking && generation === this.trackingGeneration) {
            this.handleWatchGeolocationError(error)
          }
        },
      )
      if (!this.tracking || generation !== this.trackingGeneration) {
        try {
          await clearWatch(nextHandle)
        } catch (error) {
          logService.warn(LOG_CATEGORIES.MAP, 'Failed to clear stale location watch', { error })
        }
        return
      }
      this.watchHandle = nextHandle
    } catch (error) {
      if (this.tracking && generation === this.trackingGeneration) {
        this.handleGeolocationError(error as GeoError)
      }
    }
  }

  // Stop tracking user location. `persistFollowState: false` skips clearing the
  // persisted follow mode for teardown stops (onRemove), so an app suspend or
  // style-driven control re-add does not erase the user's follow intent.
  private async stopTracking(options: { persistFollowState?: boolean } = {}): Promise<void> {
    this.tracking = false
    this.trackingGeneration += 1
    if (options.persistFollowState !== false) {
      persistFollowLocationState(false)
    }
    // Remove active state from button
    const locateBtn = this.container.querySelector('.maplibregl-ctrl-geolocate') as HTMLElement
    if (locateBtn) locateBtn.classList.remove('active')
    if (this.map) {
      setFollowLocationZoomAnchor(this.map, false)
    }
    setPassiveLocationMarkerSuppressed(false)

    // Stop watching position
    if (this.watchHandle) {
      const handle = this.watchHandle
      this.watchHandle = null
      try {
        await clearWatch(handle)
      } catch (error) {
        logService.warn(LOG_CATEGORIES.MAP, 'Failed to stop location watch', { error })
      }
    }

    // Remove markers
    this.removeMarkers()
  }

  // Update user position on map
  private updatePosition(
    position: GeoPosition,
    options: { forceCenter?: boolean; preserveZoom?: boolean } = {},
  ): void {
    if (!this.map) return

    const { latitude, longitude, accuracy, heading } = position.coords
    const lngLat = { lng: longitude, lat: latitude }
    this.lastTrackedLngLat = lngLat
    this.accuracyRadius = accuracy
    emitCurrentLocation(position, 'tracking')

    // First positioning - fly to location
    if (options.forceCenter || !this.userLocationMarker) {
      this.map.flyTo(
        optimizeFlyToCameraOptions(this.map, {
          center: lngLat,
          zoom: options.preserveZoom ? this.map.getZoom() : 16,
          duration: 1000,
        }),
      )
    }

    // Create or update user location marker
    this.updateMarkers(lngLat, heading)
  }

  // Update location markers on the map
  private updateMarkers(lngLat: maplibregl.LngLatLike, heading: number | null | undefined): void {
    this.lastGpsHeading = normalizeLocationHeading(heading)
    // Implementation matches the original CircularGeolocateControl
    if (!this.userLocationMarker) {
      // Add marker to map
      this.userLocationMarker = createMapMarker({
        element: createUserLocationMarkerElement('tracking'),
        rotationAlignment: 'map',
        pitchAlignment: 'viewport',
      })
        .setLngLat(lngLat)
        .addTo(this.map!)
    } else {
      // Update marker position
      this.userLocationMarker.setLngLat(lngLat)
    }
    updateUserLocationMarkerHeading(
      this.userLocationMarker,
      this.deviceHeading ?? this.lastGpsHeading,
    )

    // Create or update accuracy radius
    if (this.accuracyRadius > 0) {
      if (!this.map!.getSource('accuracy-radius') && this.map) {
        // Create a translucent circle showing the accuracy radius
        const radiusAtZoom =
          this.accuracyRadius /
          Math.cos(((lngLat as maplibregl.LngLat).lat * Math.PI) / 180) /
          0.075 /
          Math.pow(2, this.map.getZoom())

        // Add accuracy circle if not already present
        this.map.addSource('accuracy-radius', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [(lngLat as maplibregl.LngLat).lng, (lngLat as maplibregl.LngLat).lat],
            },
            properties: {},
          },
        })

        this.map.addLayer({
          id: 'accuracy-radius',
          type: 'circle',
          source: 'accuracy-radius',
          paint: {
            'circle-radius': radiusAtZoom,
            'circle-color': '#1976D2',
            'circle-opacity': 0.15,
            'circle-stroke-color': '#1976D2',
            'circle-stroke-width': 1,
            'circle-stroke-opacity': 0.3,
          },
        })
      } else if (this.map && this.map.getSource('accuracy-radius')) {
        // Update the accuracy circle
        const source = this.map.getSource('accuracy-radius') as maplibregl.GeoJSONSource
        source.setData({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [(lngLat as maplibregl.LngLat).lng, (lngLat as maplibregl.LngLat).lat],
          },
          properties: {},
        })

        // Update the radius to match the current accuracy
        this.map.setPaintProperty(
          'accuracy-radius',
          'circle-radius',
          this.accuracyRadius /
            Math.cos(((lngLat as maplibregl.LngLat).lat * Math.PI) / 180) /
            0.075 /
            Math.pow(2, this.map.getZoom()),
        )
      }
    }
  }

  // Handle geolocation errors reported while a watch is already running. watchPosition
  // keeps retrying on its own, and platforms like iOS/macOS CoreLocation routinely report
  // transient hiccups here (e.g. kCLErrorDomain error 0 / locationUnknown) that resolve a
  // moment later. Only react to errors the watch cannot recover from itself; log and ignore
  // the rest so a brief blip doesn't silently cancel "follow me" tracking.
  private handleWatchGeolocationError(error: GeoError): void {
    if (
      (!isTauri() && typeof window !== 'undefined' && window.isSecureContext === false) ||
      error.code === error.PERMISSION_DENIED
    ) {
      this.handleGeolocationError(error)
      return
    }

    logService.warn(LOG_CATEGORIES.MAP, 'Transient geolocation watch error, continuing', {
      error,
    })
  }

  // Handle geolocation errors from a one-shot getCurrentPosition() call (starting tracking,
  // or fetching a fresh fix). Nothing is retrying in the background here, so these are
  // surfaced as real failures.
  private handleGeolocationError(error: GeoError): void {
    void this.stopTracking()

    // Over a non-secure origin (e.g. the LAN dev server at http://192.168.x.x) browsers — iOS
    // Safari especially — block geolocation and report it as PERMISSION_DENIED. Granting the
    // permission can't fix it; the page must be served over HTTPS (or localhost). Say that
    // instead of the generic "allow location access" message, which sends the user in circles.
    if (!isTauri() && typeof window !== 'undefined' && window.isSecureContext === false) {
      logService.warn(LOG_CATEGORIES.MAP, 'Geolocation blocked by insecure (non-HTTPS) origin', {
        error,
      })
      showError(
        'Location needs a secure (HTTPS) connection. It works on the live site and in the installed app — but not over a plain-HTTP dev address like http://192.168.x.x.',
      )
      return
    }

    if (error.code === error.PERMISSION_DENIED) {
      const message = isTauri()
        ? 'Location permission denied. Please allow location access in system settings.'
        : 'Location permission denied. Please allow location access in your browser.'

      logService.warn(LOG_CATEGORIES.MAP, 'Geolocation permission denied', { error })
      showError(message)
      return
    }

    logService.error(LOG_CATEGORIES.MAP, 'Error getting user location', { error })
    showError(`Error getting user location: ${error.message}`)
  }

  // Remove location markers from the map
  private removeMarkers(): void {
    // Remove the user location marker
    if (this.userLocationMarker) {
      this.userLocationMarker.remove()
      this.userLocationMarker = null
    }
    this.lastTrackedLngLat = null

    // Remove the accuracy radius layer and source
    if (this.map) {
      if (this.map.getLayer('accuracy-radius')) {
        this.map.removeLayer('accuracy-radius')
        this.map.removeSource('accuracy-radius')
      }
    }
  }

  private hasTrackedLocationMovedAwayFromCenter(deadbandPx: number): boolean {
    if (!this.map || !this.tracking || !this.lastTrackedLngLat) return false

    const trackedPoint = this.map.project(this.lastTrackedLngLat)
    const centerPoint = this.map.project(this.map.getCenter())
    return Math.hypot(trackedPoint.x - centerPoint.x, trackedPoint.y - centerPoint.y) > deadbandPx
  }
}

// Custom control implementation that combines all controls into a single horizontal container
class CombinedMapControls implements IControl {
  private container: HTMLElement
  private map?: maplibregl.Map
  private isFullscreen: boolean = false
  private tracking: boolean = false
  private trackingGeneration = 0
  private locationRequestGeneration: number | null = null
  private watchHandle: GeoWatchHandle | null = null
  private userLocationMarker: maplibregl.Marker | null = null
  private deviceHeading: number | null = null
  private lastGpsHeading: number | null = null
  private lastTrackedLngLat: { lng: number; lat: number } | null = null
  private accuracyRadius: number = 0
  private rotationRafId: number | null = null
  private compassIcon: HTMLElement
  private mapStyleBtn: HTMLButtonElement
  private hideBtn: HTMLButtonElement
  private mapContentWrapper?: HTMLElement

  constructor(
    private onMapStyleToggle: () => void,
    private onHideToggle: () => void,
  ) {
    // Create the main container
    this.container = document.createElement('div')
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group combined-map-controls'

    // Create the controls container that will hold all controls horizontally
    const controlsContainer = document.createElement('div')
    controlsContainer.className = 'controls-container'

    // 1. Add zoom controls (top)
    const zoomControls = this.createZoomControls()
    controlsContainer.appendChild(zoomControls)

    // 2. Add compass control
    const compassControl = this.createCompassControl()
    this.compassIcon = compassControl.querySelector('.compass-icon-wrapper') as HTMLElement
    controlsContainer.appendChild(compassControl)

    // 3. Add locate control
    const locateControl = this.createLocateControl()
    controlsContainer.appendChild(locateControl)

    // 4. Add fullscreen control (skip on Tauri � the native window handles this)
    if (!isTauri()) {
      const fullscreenControl = this.createFullscreenControl()
      controlsContainer.appendChild(fullscreenControl)
    }

    // 5. Add "Map content" control (user-facing POI categories)
    const mapContentControl = this.createMapContentControl()
    this.mapContentWrapper = mapContentControl
    controlsContainer.appendChild(mapContentControl)

    // 5b. Add layers toggle control — a developer tool (always on for now; see MAP_LAYERS_CONTROL_ENABLED).
    if (MAP_LAYERS_CONTROL_ENABLED) {
      const layersControl = this.createLayersControl()
      controlsContainer.appendChild(layersControl)
    }

    // 5c. Add show/hide map data (tracks, waypoints, POIs, photos) toggle
    const hideControl = this.createHideElementsControl()
    this.hideBtn = hideControl.querySelector('.maplibregl-ctrl-hide-elements') as HTMLButtonElement
    controlsContainer.appendChild(hideControl)

    // 6. Add map style toggle control
    const mapStyleControl = this.createMapStyleControl()
    this.mapStyleBtn = mapStyleControl.querySelector(
      '.maplibregl-ctrl-map-style',
    ) as HTMLButtonElement
    controlsContainer.appendChild(mapStyleControl)

    // Add the controls container to the main container
    this.container.appendChild(controlsContainer)
  }

  private createHideElementsControl(): HTMLElement {
    const hideWrapper = document.createElement('div')
    hideWrapper.className = 'map-ctrl-btn hide-elements-wrapper'

    const hideBtn = document.createElement('button')
    hideBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-hide-elements'
    hideBtn.setAttribute('aria-label', 'Hide tracks')
    hideBtn.setAttribute('data-testid', 'map-hide-elements-toggle')
    hideBtn.appendChild(renderIcon(Eye, { size: 20 }))

    hideBtn.addEventListener('click', () => {
      this.onHideToggle()
    })

    hideWrapper.appendChild(hideBtn)
    return hideWrapper
  }

  // Reflect the show/hide flag: Eye when tracks are visible (tap to hide), EyeOff when hidden.
  setElementsHidden(hidden: boolean): void {
    if (!this.hideBtn) return
    this.hideBtn.replaceChildren(renderIcon(hidden ? EyeOff : Eye, { size: 20 }))
    this.hideBtn.setAttribute('aria-label', hidden ? 'Show tracks' : 'Hide tracks')
    this.hideBtn.classList.toggle('is-active', hidden)
  }

  onAdd(map: maplibregl.Map): HTMLElement {
    this.map = map

    // Set up event listeners for compass rotation
    this.setupCompassEvents(map)

    return this.container
  }

  onRemove(): void {
    // Clean up event listeners
    if (this.map) {
      this.map.off('rotate', this.updateCompassRotation.bind(this))
      this.map.off('pitch', this.updateCompassRotation.bind(this))
      this.map.off('moveend', this.updateCompassRotation.bind(this))
    }

    // Stop location tracking if active (teardown: keep persisted follow mode)
    void this.stopTracking({ persistFollowState: false })

    // Clean up rotation frame
    if (this.rotationRafId !== null) {
      cancelAnimationFrame(this.rotationRafId)
      this.rotationRafId = null
    }

    this.container.parentNode?.removeChild(this.container)
    this.map = undefined
  }

  // Create the zoom controls (separate buttons)
  private createZoomControls(): HTMLElement {
    const zoomWrapper = document.createElement('div')
    zoomWrapper.className = 'zoom-controls-wrapper'

    // Create zoom in button
    const zoomInBtnWrapper = document.createElement('div')
    zoomInBtnWrapper.className = 'map-ctrl-btn'
    const zoomInBtn = document.createElement('button')
    zoomInBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-zoom-in'
    zoomInBtn.setAttribute('aria-label', 'Zoom In')
    zoomInBtn.appendChild(renderIcon(ZoomIn, { size: 20 }))
    zoomInBtn.addEventListener('click', () => {
      this.map?.easeTo({
        zoom: this.map.getZoom() + 1,
        duration: MAP_CONTROL_ZOOM_DURATION_MS,
        easing: easeOutCubic,
      })
    })
    zoomInBtnWrapper.appendChild(zoomInBtn)
    zoomWrapper.appendChild(zoomInBtnWrapper)

    // Create zoom out button
    const zoomOutBtnWrapper = document.createElement('div')
    zoomOutBtnWrapper.className = 'map-ctrl-btn'
    const zoomOutBtn = document.createElement('button')
    zoomOutBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-zoom-out'
    zoomOutBtn.setAttribute('aria-label', 'Zoom Out')
    zoomOutBtn.appendChild(renderIcon(ZoomOut, { size: 20 }))
    zoomOutBtn.addEventListener('click', () => {
      this.map?.easeTo({
        zoom: this.map.getZoom() - 1,
        duration: MAP_CONTROL_ZOOM_DURATION_MS,
        easing: easeOutCubic,
      })
    })
    zoomOutBtnWrapper.appendChild(zoomOutBtn)
    zoomWrapper.appendChild(zoomOutBtnWrapper)

    return zoomWrapper
  }

  // Create the compass control
  private createCompassControl(): HTMLElement {
    const compassWrapper = document.createElement('div')
    compassWrapper.className = 'map-ctrl-btn compass-wrapper'

    const compassBtn = document.createElement('button')
    compassBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-compass'
    compassBtn.setAttribute('aria-label', 'Reset Bearing to North')

    const compassIconWrapper = document.createElement('div')
    compassIconWrapper.className = 'compass-icon-wrapper'
    compassIconWrapper.appendChild(renderIcon(Navigation, { size: 20 }))

    compassBtn.appendChild(compassIconWrapper)

    compassBtn.addEventListener('click', () => {
      if (this.map && this.map.getBearing() !== 0) {
        this.map.easeTo({
          bearing: 0,
          pitch: 0,
          duration: 500,
        })
      }
    })

    compassWrapper.appendChild(compassBtn)
    return compassWrapper
  }

  // Create the "Map content" toggle control (POI categories) for desktop
  private createMapContentControl(): HTMLElement {
    const wrapper = document.createElement('div')
    wrapper.className = 'map-ctrl-btn map-content-wrapper'

    const btn = document.createElement('button')
    btn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-map-content'
    btn.setAttribute('aria-label', 'Toggle Map content')
    btn.appendChild(renderIcon(MapPinned, { size: 20 }))

    btn.addEventListener('click', () => {
      uiStore.toggleMapContentPanel()
    })

    wrapper.appendChild(btn)
    return wrapper
  }

  setMapContentVisible(visible: boolean): void {
    if (this.mapContentWrapper) this.mapContentWrapper.style.display = visible ? '' : 'none'
  }

  // Create the layers toggle control for desktop
  private createLayersControl(): HTMLElement {
    const layersWrapper = document.createElement('div')
    layersWrapper.className = 'map-ctrl-btn layers-wrapper'

    const layersBtn = document.createElement('button')
    layersBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-layers'
    layersBtn.setAttribute('aria-label', 'Toggle Map Layers Panel')
    layersBtn.appendChild(renderIcon(Layers, { size: 20 }))

    layersBtn.addEventListener('click', () => {
      uiStore.toggleLayersPanel()
    })

    layersWrapper.appendChild(layersBtn)
    return layersWrapper
  }

  private createMapStyleControl(): HTMLElement {
    const mapStyleWrapper = document.createElement('div')
    mapStyleWrapper.className = 'map-ctrl-btn map-style-wrapper'

    const mapStyleBtn = document.createElement('button')
    mapStyleBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-map-style'
    mapStyleBtn.setAttribute('aria-label', 'Toggle Map Style Panel')
    mapStyleBtn.appendChild(renderIcon(MapIcon, { size: 20 }))

    mapStyleBtn.addEventListener('click', () => {
      this.onMapStyleToggle()
    })

    mapStyleWrapper.appendChild(mapStyleBtn)
    return mapStyleWrapper
  }

  setMapStyleActive(active: boolean): void {
    if (!this.mapStyleBtn) return
    if (active) {
      this.mapStyleBtn.classList.add('is-active')
    } else {
      this.mapStyleBtn.classList.remove('is-active')
    }
  }

  setDeviceHeading(heading: number | null): void {
    this.deviceHeading = heading
    if (this.userLocationMarker) {
      updateUserLocationMarkerHeading(this.userLocationMarker, heading ?? this.lastGpsHeading)
    }
  }

  // Set up compass rotation events
  private setupCompassEvents(map: maplibregl.Map): void {
    const rotationHandler = this.updateCompassRotation.bind(this)
    map.on('rotate', rotationHandler)
    map.on('pitch', rotationHandler)
    map.on('moveend', rotationHandler)

    // Initial rotation update - wait for map to be fully loaded
    map.on('load', () => {
      this.updateCompassRotation()
    })

    // Also update immediately if map is already loaded
    if (map.loaded()) {
      this.updateCompassRotation()
    }
  }

  // Update the compass rotation based on map bearing
  private updateCompassRotation(): void {
    if (this.rotationRafId !== null) {
      cancelAnimationFrame(this.rotationRafId)
    }

    this.rotationRafId = requestAnimationFrame(() => {
      if (!this.map) return

      const bearing = this.map.getBearing()
      // Apply both the base rotation (-45deg) and the bearing rotation
      this.compassIcon.style.transform = `rotate(${-45 - bearing}deg)`

      const compassBtn = this.compassIcon.closest('.maplibregl-ctrl-compass') as HTMLElement
      if (bearing === 0) {
        compassBtn.classList.remove('is-active')
      } else {
        compassBtn.classList.add('is-active')
      }
    })
  }

  // Create the locate control
  private createLocateControl(): HTMLElement {
    const locateWrapper = document.createElement('div')
    locateWrapper.className = 'map-ctrl-btn locate-wrapper'

    const locateBtn = document.createElement('button')
    locateBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-geolocate'
    locateBtn.setAttribute('aria-label', 'Find my location')
    locateBtn.appendChild(renderIcon(Locate, { size: 20 }))

    locateBtn.addEventListener('click', () => {
      void deviceHeadingService.requestPermission()
      void this.toggleGeolocation()
    })

    locateWrapper.appendChild(locateBtn)
    return locateWrapper
  }

  private async toggleGeolocation(): Promise<void> {
    if (!this.map) return

    // If already tracking, stop tracking
    if (this.tracking) {
      void this.stopTracking()
      return
    }

    // Start tracking user's location
    await this.startTracking()
  }

  // Start tracking user location
  async focusCurrentLocation(
    options: { preserveZoom?: boolean; allowStart?: boolean } = {},
  ): Promise<void> {
    if (!this.map) return
    if (!this.tracking) {
      if (options.allowStart === false) return
      await this.startTracking({ forceCenter: true, preserveZoom: options.preserveZoom })
      return
    }

    const generation = this.trackingGeneration
    if (this.locationRequestGeneration === generation) return
    this.locationRequestGeneration = generation
    try {
      const position = await getCurrentPosition(GEOLOCATION_OPTIONS)
      if (!this.tracking || generation !== this.trackingGeneration) return
      this.updatePosition(position, { forceCenter: true, preserveZoom: options.preserveZoom })
      await this.restartTrackingWatch(generation)
    } catch (error) {
      if (this.tracking && generation === this.trackingGeneration) {
        this.handleGeolocationError(error as GeoError)
      } else {
        logService.debug(LOG_CATEGORIES.MAP, 'Ignoring stale location request error', { error })
      }
    } finally {
      if (this.locationRequestGeneration === generation) {
        this.locationRequestGeneration = null
      }
    }
  }

  isFollowLocationActive(): boolean {
    return this.tracking
  }

  async stopFollowLocation(): Promise<void> {
    await this.stopTracking()
  }

  async stopFollowLocationIfPannedAway(deadbandPx: number): Promise<boolean> {
    if (!this.hasTrackedLocationMovedAwayFromCenter(deadbandPx)) {
      return false
    }

    await this.stopTracking()
    return true
  }

  private async startTracking(
    options: { forceCenter?: boolean; preserveZoom?: boolean } = {},
  ): Promise<void> {
    if (!this.map) {
      return
    }
    if (!isGeolocationSupported()) {
      showError('Geolocation is not supported on this device')
      return
    }

    // Set button to active state
    const locateBtn = this.container.querySelector('.maplibregl-ctrl-geolocate') as HTMLElement
    locateBtn.classList.add('active')
    const generation = ++this.trackingGeneration
    this.locationRequestGeneration = generation
    this.tracking = true
    setFollowLocationZoomAnchor(this.map, true)
    setPassiveLocationMarkerSuppressed(true)
    persistFollowLocationState(true)

    try {
      const position = await getCurrentPosition(GEOLOCATION_OPTIONS)
      if (!this.tracking || generation !== this.trackingGeneration) return
      this.updatePosition(position, {
        forceCenter: options.forceCenter,
        preserveZoom: options.preserveZoom,
      })
      await this.restartTrackingWatch(generation)
    } catch (error) {
      if (this.tracking && generation === this.trackingGeneration) {
        this.handleGeolocationError(error as GeoError)
      } else {
        logService.debug(LOG_CATEGORIES.MAP, 'Ignoring stale location request error', { error })
      }
    } finally {
      if (this.locationRequestGeneration === generation) {
        this.locationRequestGeneration = null
      }
    }
  }

  private async restartTrackingWatch(generation = this.trackingGeneration): Promise<void> {
    if (this.watchHandle) {
      const previousHandle = this.watchHandle
      this.watchHandle = null
      try {
        await clearWatch(previousHandle)
      } catch (error) {
        logService.warn(LOG_CATEGORIES.MAP, 'Failed to clear existing location watch', { error })
      }
    }

    if (!this.tracking || generation !== this.trackingGeneration) return

    try {
      const nextHandle = await watchPosition(
        GEOLOCATION_OPTIONS,
        (position) => {
          if (this.tracking && generation === this.trackingGeneration) {
            this.updatePosition(position)
          }
        },
        (error) => {
          if (this.tracking && generation === this.trackingGeneration) {
            this.handleWatchGeolocationError(error)
          }
        },
      )
      if (!this.tracking || generation !== this.trackingGeneration) {
        try {
          await clearWatch(nextHandle)
        } catch (error) {
          logService.warn(LOG_CATEGORIES.MAP, 'Failed to clear stale location watch', { error })
        }
        return
      }
      this.watchHandle = nextHandle
    } catch (error) {
      if (this.tracking && generation === this.trackingGeneration) {
        this.handleGeolocationError(error as GeoError)
      }
    }
  }

  // Stop tracking user location. `persistFollowState: false` skips clearing the
  // persisted follow mode for teardown stops (onRemove), so an app suspend or
  // style-driven control re-add does not erase the user's follow intent.
  private async stopTracking(options: { persistFollowState?: boolean } = {}): Promise<void> {
    this.tracking = false
    this.trackingGeneration += 1
    if (options.persistFollowState !== false) {
      persistFollowLocationState(false)
    }
    // Remove active state from button
    const locateBtn = this.container.querySelector('.maplibregl-ctrl-geolocate') as HTMLElement
    if (locateBtn) locateBtn.classList.remove('active')
    if (this.map) {
      setFollowLocationZoomAnchor(this.map, false)
    }
    setPassiveLocationMarkerSuppressed(false)

    // Stop watching position
    if (this.watchHandle) {
      const handle = this.watchHandle
      this.watchHandle = null
      try {
        await clearWatch(handle)
      } catch (error) {
        logService.warn(LOG_CATEGORIES.MAP, 'Failed to stop location watch', { error })
      }
    }

    // Remove markers
    this.removeMarkers()
  }

  // Update user position on map
  private updatePosition(
    position: GeoPosition,
    options: { forceCenter?: boolean; preserveZoom?: boolean } = {},
  ): void {
    if (!this.map) return

    const { latitude, longitude, accuracy, heading } = position.coords
    const lngLat = { lng: longitude, lat: latitude }
    this.lastTrackedLngLat = lngLat
    this.accuracyRadius = accuracy
    emitCurrentLocation(position, 'tracking')

    // First positioning - fly to location
    if (options.forceCenter || !this.userLocationMarker) {
      this.map.flyTo(
        optimizeFlyToCameraOptions(this.map, {
          center: lngLat,
          zoom: options.preserveZoom ? this.map.getZoom() : 16,
          duration: 1000,
        }),
      )
    }

    // Create or update user location marker
    this.updateMarkers(lngLat, heading)
  }

  // Update location markers on the map
  private updateMarkers(lngLat: maplibregl.LngLatLike, heading: number | null | undefined): void {
    this.lastGpsHeading = normalizeLocationHeading(heading)
    // Implementation matches the original CircularGeolocateControl
    if (!this.userLocationMarker) {
      // Add marker to map
      this.userLocationMarker = createMapMarker({
        element: createUserLocationMarkerElement('tracking'),
        rotationAlignment: 'map',
        pitchAlignment: 'viewport',
      })
        .setLngLat(lngLat)
        .addTo(this.map!)
    } else {
      // Update marker position
      this.userLocationMarker.setLngLat(lngLat)
    }
    updateUserLocationMarkerHeading(
      this.userLocationMarker,
      this.deviceHeading ?? this.lastGpsHeading,
    )

    // Create or update accuracy radius
    if (this.accuracyRadius > 0) {
      if (!this.map!.getSource('accuracy-radius') && this.map) {
        // Create a translucent circle showing the accuracy radius
        const radiusAtZoom =
          this.accuracyRadius /
          Math.cos(((lngLat as maplibregl.LngLat).lat * Math.PI) / 180) /
          0.075 /
          Math.pow(2, this.map.getZoom())

        // Add accuracy circle if not already present
        this.map.addSource('accuracy-radius', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [(lngLat as maplibregl.LngLat).lng, (lngLat as maplibregl.LngLat).lat],
            },
            properties: {},
          },
        })

        this.map.addLayer({
          id: 'accuracy-radius',
          type: 'circle',
          source: 'accuracy-radius',
          paint: {
            'circle-radius': radiusAtZoom,
            'circle-color': '#1976D2',
            'circle-opacity': 0.15,
            'circle-stroke-color': '#1976D2',
            'circle-stroke-width': 1,
            'circle-stroke-opacity': 0.3,
          },
        })
      } else if (this.map && this.map.getSource('accuracy-radius')) {
        // Update the accuracy circle
        const source = this.map.getSource('accuracy-radius') as maplibregl.GeoJSONSource
        source.setData({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [(lngLat as maplibregl.LngLat).lng, (lngLat as maplibregl.LngLat).lat],
          },
          properties: {},
        })

        // Update the radius to match the current accuracy
        this.map.setPaintProperty(
          'accuracy-radius',
          'circle-radius',
          this.accuracyRadius /
            Math.cos(((lngLat as maplibregl.LngLat).lat * Math.PI) / 180) /
            0.075 /
            Math.pow(2, this.map.getZoom()),
        )
      }
    }
  }

  // Handle geolocation errors reported while a watch is already running. watchPosition
  // keeps retrying on its own, and platforms like iOS/macOS CoreLocation routinely report
  // transient hiccups here (e.g. kCLErrorDomain error 0 / locationUnknown) that resolve a
  // moment later. Only react to errors the watch cannot recover from itself; log and ignore
  // the rest so a brief blip doesn't silently cancel "follow me" tracking.
  private handleWatchGeolocationError(error: GeoError): void {
    if (
      (!isTauri() && typeof window !== 'undefined' && window.isSecureContext === false) ||
      error.code === error.PERMISSION_DENIED
    ) {
      this.handleGeolocationError(error)
      return
    }

    logService.warn(LOG_CATEGORIES.MAP, 'Transient geolocation watch error, continuing', {
      error,
    })
  }

  // Handle geolocation errors from a one-shot getCurrentPosition() call (starting tracking,
  // or fetching a fresh fix). Nothing is retrying in the background here, so these are
  // surfaced as real failures.
  private handleGeolocationError(error: GeoError): void {
    void this.stopTracking()

    // Over a non-secure origin (e.g. the LAN dev server at http://192.168.x.x) browsers — iOS
    // Safari especially — block geolocation and report it as PERMISSION_DENIED. Granting the
    // permission can't fix it; the page must be served over HTTPS (or localhost). Say that
    // instead of the generic "allow location access" message, which sends the user in circles.
    if (!isTauri() && typeof window !== 'undefined' && window.isSecureContext === false) {
      logService.warn(LOG_CATEGORIES.MAP, 'Geolocation blocked by insecure (non-HTTPS) origin', {
        error,
      })
      showError(
        'Location needs a secure (HTTPS) connection. It works on the live site and in the installed app — but not over a plain-HTTP dev address like http://192.168.x.x.',
      )
      return
    }

    if (error.code === error.PERMISSION_DENIED) {
      const message = isTauri()
        ? 'Location permission denied. Please allow location access in system settings.'
        : 'Location permission denied. Please allow location access in your browser.'

      logService.warn(LOG_CATEGORIES.MAP, 'Geolocation permission denied', { error })
      showError(message)
      return
    }

    logService.error(LOG_CATEGORIES.MAP, 'Error getting user location', { error })
    showError(`Error getting user location: ${error.message}`)
  }

  // Remove location markers from the map
  private removeMarkers(): void {
    // Remove the user location marker
    if (this.userLocationMarker) {
      this.userLocationMarker.remove()
      this.userLocationMarker = null
    }
    this.lastTrackedLngLat = null

    // Remove the accuracy radius layer and source
    if (this.map) {
      if (this.map.getLayer('accuracy-radius')) {
        this.map.removeLayer('accuracy-radius')
        this.map.removeSource('accuracy-radius')
      }
    }
  }

  private hasTrackedLocationMovedAwayFromCenter(deadbandPx: number): boolean {
    if (!this.map || !this.tracking || !this.lastTrackedLngLat) return false

    const trackedPoint = this.map.project(this.lastTrackedLngLat)
    const centerPoint = this.map.project(this.map.getCenter())
    return Math.hypot(trackedPoint.x - centerPoint.x, trackedPoint.y - centerPoint.y) > deadbandPx
  }

  // Create the fullscreen control
  private createFullscreenControl(): HTMLElement {
    const fullscreenWrapper = document.createElement('div')
    fullscreenWrapper.className = 'map-ctrl-btn fullscreen-wrapper'

    const fullscreenBtn = document.createElement('button')
    fullscreenBtn.className = 'maplibregl-ctrl-icon maplibregl-ctrl-fullscreen'
    fullscreenBtn.setAttribute('aria-label', 'Toggle Fullscreen')
    fullscreenBtn.appendChild(renderIcon(Maximize, { size: 20 }))

    fullscreenBtn.addEventListener('click', () => {
      const container = document.getElementById('map-fullscreen-container') || document.body

      if (!this.isFullscreen) {
        if (container.requestFullscreen) {
          container
            .requestFullscreen()
            .then(() => {
              this.isFullscreen = true
              // Replace icon
              fullscreenBtn.innerHTML = ''
              fullscreenBtn.appendChild(renderIcon(Minimize, { size: 20 }))
            })
            .catch((err) => {
              showError(
                `Error attempting to enable fullscreen: ${err instanceof Error ? err.message : String(err)}`,
              )
            })
        }
      } else {
        if (document.exitFullscreen) {
          document
            .exitFullscreen()
            .then(() => {
              this.isFullscreen = false
              // Replace icon
              fullscreenBtn.innerHTML = ''
              fullscreenBtn.appendChild(renderIcon(Maximize, { size: 20 }))
            })
            .catch((err) => {
              showError(
                `Error attempting to exit fullscreen: ${err instanceof Error ? err.message : String(err)}`,
              )
            })
        }
      }
    })

    // Add fullscreen change event listener
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement
      // Update icon based on fullscreen state
      const btn = this.container.querySelector('.maplibregl-ctrl-fullscreen') as HTMLElement
      if (btn) {
        btn.innerHTML = ''
        btn.appendChild(renderIcon(this.isFullscreen ? Minimize : Maximize, { size: 18 }))
      }
    })

    fullscreenWrapper.appendChild(fullscreenBtn)
    return fullscreenWrapper
  }
}

const emit = defineEmits<{
  'map-loaded': [map: maplibregl.Map]
  'map-click': [event: MapMouseEvent]
  'map-interaction-start': []
  'map-pan-start': []
  'map-move': [
    {
      center: [number, number]
      zoom: number
      pitch: number
      bearing: number
      userInitiated?: boolean
    },
  ]
  'current-location': [location: CurrentLocationUpdate]
}>()

const emitCurrentLocation = (position: GeoPosition, source: CurrentLocationSource): void => {
  emit('current-location', {
    lat: position.coords.latitude,
    lon: position.coords.longitude,
    accuracyM: position.coords.accuracy,
    timestamp: position.timestamp,
    source,
  })
}

const mapStore = useMapStore()

const mapContainer = ref<HTMLElement | null>(null)
const rootEl = ref<HTMLElement | null>(null)
const mapInstance = shallowRef<maplibregl.Map | null>(null)
const liveAttributionHtml = ref('')
const mapLoaded = ref(false)
const mapStyleRevision = ref(0)

// Passive "user position circle": a clean small dot showing the current
// location whenever it is available, independent of the locate button.
let passiveLocationMarker: maplibregl.Marker | null = null
let passiveGpsHeading: number | null = null
let passiveLocationWatch: GeoWatchHandle | null = null
let passivePermissionStatus: PermissionStatus | null = null
let passivePermissionState: GeoPermissionState | null = null
let passivePermissionChangeHandler: (() => void) | null = null
let currentDeviceHeading: number | null = null
let unsubscribeDeviceHeading: (() => void) | null = null
// While the locate button is actively tracking it shows its own pulse + accuracy
// marker, so the passive circle is hidden to avoid two overlapping indicators.
let suppressPassiveLocationMarker = false
const mapStyleControlHost = shallowRef<MapStyleControlHost | null>(null)
// The mobile control cluster hosts the immersive "Full map" toggle (mobile-only feature).
const immersiveControlHost = shallowRef<MobileMapControls | null>(null)
// Whichever control cluster is mounted (mobile or desktop) hosts the show/hide-tracks eye toggle.
interface ElementsVisibilityControlHost {
  setElementsHidden(hidden: boolean): void
}
const elementsVisibilityControlHost = shallowRef<ElementsVisibilityControlHost | null>(null)
// The "Map content" (POI categories) button is only relevant inside a collection, where POIs can be
// added — so the cluster shows/hides it based on the route.
interface MapContentControlHost {
  setMapContentVisible(visible: boolean): void
}
const mapContentControlHost = shallowRef<MapContentControlHost | null>(null)
// Backed by uiStore so the three map drawers (type/layers/content) stay mutually exclusive — opening
// the basemap picker closes the others, and vice versa. Setter routes through the store's actions.
const isMapStylePanelOpen = computed<boolean>({
  get: () => uiStore.isMapStylePanelVisible,
  set: (open) => (open ? uiStore.showMapStylePanel() : uiStore.hideMapStylePanel()),
})
const isMapStyleReloading = ref(false)
const customStyleUrlInput = ref('')
const customStyleJsonInput = ref('')
const customStyleFileName = ref<string | null>(null)
const customStyleFileInput = ref<HTMLInputElement | null>(null)
const isCustomStyleOptionsVisible = ref(false)
const customStyleNameInput = ref('')
const sessionCustomStyles = ref<SessionCustomStyleEntry[]>([...sessionCustomStylesStore])
let mapStyleRequestId = 0
let currentMapStyleTargetKey: string | null = null
let pendingMapStyleTargetKey: string | null = null
let unlistenTauriResume: (() => void) | null = null
let isSettingUpTauriResumeListener = false
let isTauriResumeListenerCancelled = false

const mapStyleCards = computed<MapStyleCard[]>(() => {
  const builtInCards: MapStyleCard[] = MAP_STYLE_OPTIONS.map((option) => ({
    id: `builtin-${option.id}`,
    label: option.label,
    description: option.description,
    cardType: 'builtin',
    builtInStyleId: option.id,
  }))

  const sessionCards: MapStyleCard[] = [...sessionCustomStyles.value]
    .sort((a, b) => b.id - a.id)
    .map((entry) => ({
      id: `session-${entry.id}`,
      label: entry.name,
      description: entry.customStyleJson ? 'Session JSON style' : 'Session URL style',
      cardType: 'session',
      sessionStyleId: entry.id,
    }))

  return [...sessionCards, ...builtInCards]
})

const getInitialMapStylePreference = (): MapStylePreference => {
  const currentStorePreference = mapStore.mapStylePreference
  if (currentStorePreference) {
    return normalizeMapStylePreference(currentStorePreference)
  }

  return { ...DEFAULT_MAP_STYLE_PREFERENCE }
}

const mapStylePreference = ref<MapStylePreference>(getInitialMapStylePreference())
mapStore.setMapStylePreference(mapStylePreference.value)

if (mapStylePreference.value.styleId === 'custom') {
  customStyleUrlInput.value = mapStylePreference.value.customStyleUrl ?? ''
  customStyleJsonInput.value = mapStylePreference.value.customStyleJson ?? ''
}

provide('mapInstance', mapInstance)
provide('mapLoaded', mapLoaded)
provide('mapStyleRevision', mapStyleRevision)
const uiStore = useUiStore()
const offlineStore = useOfflineStore()
const recordingStore = useRecordingStore()
const workspaceStore = useWorkspaceStore()

const serializeRecord = (record: Record<string, string | number>): string =>
  Object.entries(record)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}:${String(value)}`)
    .join(',')

const getMapStylePreferenceKey = (preference: MapStylePreference): string => {
  const normalized = normalizeMapStylePreference(preference)
  return JSON.stringify({
    styleId: normalized.styleId,
    customStyleUrl: normalized.customStyleUrl ?? null,
    customStyleJson: normalized.customStyleJson ?? null,
  })
}

const getOfflineMapIndexKey = (): string =>
  Object.values(offlineStore.index)
    .sort((left, right) => left.workspaceId.localeCompare(right.workspaceId))
    .map((entry) =>
      [
        entry.workspaceId,
        entry.status,
        entry.downloadedAt,
        entry.styleId,
        entry.workspaceHash,
        entry.sourceIds.join(','),
        serializeRecord(entry.sourceExtensions),
        serializeRecord(entry.sourceMinzooms),
        serializeRecord(entry.sourceMaxzooms),
      ].join(':'),
    )
    .join('|')

const getOfflineMapEntry = (preference: MapStylePreference): OfflineEntry | null => {
  if (connectivityService.isEffectivelyOnline.value) return null

  const activeEntry = offlineStore.getEntry(workspaceStore.clientId)
  if (activeEntry?.status === 'ready' && activeEntry.sourceIds.length > 0) {
    return activeEntry
  }

  // Without a usable active package (including Overview and Library), use any ready package as a
  // gateway to the shared durable package tile store, preferring the current map style.
  const readyEntries = Object.values(offlineStore.index)
    .filter((entry) => entry.status === 'ready' && entry.sourceIds.length > 0)
    .sort((left, right) => right.downloadedAt - left.downloadedAt)
  const preferredStyleId = getOfflineStyleCacheKey(preference)

  return (
    readyEntries.find((entry) => entry.styleId === preferredStyleId) ??
    readyEntries.find(
      (entry) => mapStylePreferenceFromOfflineStyleCacheKey(entry.styleId) !== null,
    ) ??
    null
  )
}

const getResolvedMapStyleTargetKey = (preference: MapStylePreference): string => {
  const onlineStyleKey = `online:${getMapStylePreferenceKey(preference)}`
  if (!isTauri()) {
    return onlineStyleKey
  }

  const entry = getOfflineMapEntry(preference)
  if (!entry || entry.sourceIds.length === 0) {
    return onlineStyleKey
  }

  const offlinePreference = mapStylePreferenceFromOfflineStyleCacheKey(entry.styleId)
  return [
    'offline',
    entry.workspaceId,
    entry.styleId,
    entry.workspaceHash,
    entry.sourceIds.join(','),
    serializeRecord(entry.sourceExtensions),
    serializeRecord(entry.sourceMinzooms),
    serializeRecord(entry.sourceMaxzooms),
    getMapStylePreferenceKey(offlinePreference ?? preference),
  ].join('|')
}

const openStaticPage = (pageId: string) => {
  window.dispatchEvent(new CustomEvent('open-static-site', { detail: pageId }))
}

const createDirectionArrow = () => {
  if (!mapInstance.value) return

  if (mapInstance.value.hasImage('direction-arrow')) {
    mapInstance.value.removeImage('direction-arrow')
  }

  // Compact route direction marker. Keep it neutral so it works on any track color.
  const canvas = document.createElement('canvas')
  const size = 16
  canvas.width = size
  canvas.height = size

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.moveTo(3, size / 2 - 4)
  ctx.lineTo(size - 3, size / 2)
  ctx.lineTo(3, size / 2 + 4)
  ctx.closePath()
  ctx.fill()

  ctx.strokeStyle = '#111827'
  ctx.lineWidth = 1.35
  ctx.lineJoin = 'round'
  ctx.stroke()

  mapInstance.value.addImage('direction-arrow', {
    data: ctx.getImageData(0, 0, size, size).data,
    width: size,
    height: size,
  })
}

const updateLiveAttribution = () => {
  const container = mapInstance.value?.getContainer()
  if (!container) {
    liveAttributionHtml.value = ''
    return
  }

  const inner = container.querySelector('.maplibregl-ctrl-attrib-inner')
  liveAttributionHtml.value = inner instanceof HTMLElement ? inner.innerHTML.trim() : ''
}

const closeMapStylePanel = () => {
  isMapStylePanelOpen.value = false
  isCustomStyleOptionsVisible.value = false
}

const toggleMapStylePanel = () => {
  const nextOpen = !isMapStylePanelOpen.value
  isMapStylePanelOpen.value = nextOpen

  // Always start with cards-only view when opening, and fully reset when closing.
  isCustomStyleOptionsVisible.value = false
}

const persistMapStylePreference = (preference: MapStylePreference) => {
  // Intentionally no-op: style selection should remain session-scoped only.
  void preference
}

const normalizeHttpsStyleUrl = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  if (!trimmed) {
    return null
  }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    throw new Error('Custom style URL must be a valid HTTPS URL')
  }

  if (parsed.protocol !== 'https:') {
    throw new Error('Custom style URL must use HTTPS')
  }

  return trimmed
}

const applyMapStylePreference = (nextPreference: MapStylePreference) => {
  const normalized = normalizeMapStylePreference(nextPreference)

  if (normalized.styleId === 'custom' && normalized.customStyleUrl) {
    try {
      normalized.customStyleUrl = normalizeHttpsStyleUrl(normalized.customStyleUrl)
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Custom style URL must use HTTPS')
      return
    }
  }

  if (normalized.styleId === 'custom' && normalized.customStyleJson) {
    try {
      parseCustomMapStyleJson(normalized.customStyleJson)
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Invalid custom map style JSON')
      return
    }
  }

  mapStylePreference.value = normalized
  mapStore.setMapStylePreference(normalized)
  persistMapStylePreference(normalized)

  if (normalized.styleId === 'custom') {
    customStyleUrlInput.value = normalized.customStyleUrl ?? ''
    customStyleJsonInput.value = normalized.customStyleJson ?? ''
  }

  if (isE2eMode()) {
    return
  }

  if (!mapInstance.value) {
    return
  }

  void setResolvedMapStyle(normalized).catch((error) => {
    logService.error(LOG_CATEGORIES.MAP, 'Failed to switch map style', {
      nextPreference: normalized,
      error,
    })
    showError(
      `Failed to switch map style: ${error instanceof Error ? error.message : String(error)}`,
    )
  })
}

const buildResolvedMapStyle = async (
  preference: MapStylePreference,
): Promise<{
  style: Exclude<MapOptions['style'], undefined>
  terrain: TerrainSpecification | null
}> => {
  const normalizeStyleWithoutTerrain = (
    style: Exclude<MapOptions['style'], undefined>,
  ): { style: Exclude<MapOptions['style'], undefined>; terrain: TerrainSpecification | null } => {
    if (typeof style === 'string' || !('terrain' in style)) {
      return { style, terrain: null }
    }

    const { terrain, ...styleWithoutTerrain } = style
    return {
      style: styleWithoutTerrain as Exclude<MapOptions['style'], undefined>,
      terrain: terrain ? { ...terrain } : null,
    }
  }

  if (!isTauri()) {
    setRoutecampContourOfflineContext(null)
    return normalizeStyleWithoutTerrain(await resolveRuntimeMapStyle(preference))
  }

  const entry = getOfflineMapEntry(preference)

  // Contour DEM fetches bypass the offline style rewrite below, so route them
  // through the offline store whenever the package includes the terrain source.
  const offlineTerrainAvailable = Boolean(entry?.sourceIds.includes(HILLSHADE_SOURCE_ID))
  setRoutecampContourOfflineContext(
    entry && offlineTerrainAvailable
      ? {
          styleId: entry.styleId,
          sourceId: HILLSHADE_SOURCE_ID,
          ext: entry.sourceExtensions[HILLSHADE_SOURCE_ID] ?? 'webp',
        }
      : null,
  )

  if (!entry) return normalizeStyleWithoutTerrain(await resolveRuntimeMapStyle(preference))

  const offlinePreference = mapStylePreferenceFromOfflineStyleCacheKey(entry.styleId)
  const sourceIds = entry.sourceIds
  if (sourceIds.length === 0) {
    return normalizeStyleWithoutTerrain(
      offlinePreference
        ? await resolveRuntimeMapStyle(offlinePreference)
        : await resolveRuntimeMapStyle(preference),
    )
  }

  const offlineResolvedStyle = await resolveRuntimeMapStyle(offlinePreference ?? preference)

  return normalizeStyleWithoutTerrain(
    (await applyOfflineRewrite(
      offlineResolvedStyle,
      entry.workspaceId,
      entry.styleId,
      sourceIds,
      entry.sourceExtensions,
      entry.sourceMinzooms,
      entry.sourceMaxzooms,
    )) as Exclude<MapOptions['style'], undefined>,
  )
}

let activeTerrainSpecification: TerrainSpecification | null = null

const setActiveTerrainSpecification = (terrain: TerrainSpecification | null) => {
  activeTerrainSpecification = terrain ? { ...terrain } : null
}

const shouldEnableTerrain = (map: maplibregl.Map): boolean => {
  return (
    !!activeTerrainSpecification &&
    map.getPitch() >= TERRAIN_ENABLE_PITCH &&
    map.getZoom() >= TERRAIN_ENABLE_ZOOM
  )
}

const shouldDisableTerrain = (map: maplibregl.Map): boolean => {
  return map.getPitch() <= TERRAIN_DISABLE_PITCH || map.getZoom() < TERRAIN_DISABLE_ZOOM
}

const syncPitchZoomTerrain = (map: maplibregl.Map) => {
  const hasTerrain = !!map.getTerrain()

  if (!activeTerrainSpecification) {
    if (hasTerrain) {
      map.setTerrain(null)
    }
    return
  }

  if (hasTerrain) {
    if (shouldDisableTerrain(map)) {
      map.setTerrain(null)
    }
    return
  }

  if (shouldEnableTerrain(map)) {
    map.setTerrain({ ...activeTerrainSpecification })
  }
}

function waitForAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve())
  })
}

function waitForNextStyleLoad(map: maplibregl.Map): { cancel: () => void; promise: Promise<void> } {
  let listener: () => void
  const promise = new Promise<void>((resolve) => {
    listener = () => resolve()
    map.once('style.load', listener)
  })

  return {
    cancel: () => map.off('style.load', listener),
    promise,
  }
}

async function applyRoutecampContoursWithRetry(map: maplibregl.Map): Promise<void> {
  if (applyRoutecampContours(map)) {
    return
  }

  await waitForAnimationFrame()
  applyRoutecampContours(map)
}

async function completeMapStyleRefresh(map: maplibregl.Map, requestId: number): Promise<void> {
  if (map !== mapInstance.value || requestId !== mapStyleRequestId) {
    return
  }

  try {
    await applyRoutecampContoursWithRetry(map)
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to apply Routecamp contours after style load', {
      error,
    })
  }

  if (map !== mapInstance.value || requestId !== mapStyleRequestId) {
    return
  }

  mapStore.initializeLayers()
  mapStyleRevision.value += 1
  createDirectionArrow()
  syncPitchZoomTerrain(map)
  requestAnimationFrame(() => resizeMap())
}

const setResolvedMapStyle = async (preference: MapStylePreference) => {
  if (!mapInstance.value) return

  const normalized = normalizeMapStylePreference(preference)
  const targetKey = getResolvedMapStyleTargetKey(normalized)
  if (currentMapStyleTargetKey === targetKey && pendingMapStyleTargetKey === null) {
    return
  }

  const requestId = ++mapStyleRequestId
  pendingMapStyleTargetKey = targetKey
  try {
    isMapStyleReloading.value = true

    const nextStyle = await buildResolvedMapStyle(normalized)
    const map = mapInstance.value

    // Force a full style reload for runtime style swaps. Large third-party
    // styles can stall when MapLibre attempts diff-based merging.
    if (!map || requestId !== mapStyleRequestId) return

    // MapLibre 5.x can crash in its terrain shader path when setStyle() runs
    // during active camera motion or while delegated hover queries keep firing.
    if (map.isMoving()) {
      map.stop()
      await waitForAnimationFrame()
    }

    if (map !== mapInstance.value || requestId !== mapStyleRequestId) {
      return
    }

    const previousTerrain = map.getTerrain()
    if (previousTerrain) {
      map.setTerrain(null)
      await waitForAnimationFrame()

      if (map !== mapInstance.value || requestId !== mapStyleRequestId) {
        map.setTerrain(previousTerrain)
        return
      }
    }

    const styleLoad = waitForNextStyleLoad(map)

    try {
      map.setStyle(nextStyle.style, { diff: false })
    } catch (error) {
      styleLoad.cancel()
      if (previousTerrain) {
        map.setTerrain(previousTerrain)
      }
      throw error
    }

    setActiveTerrainSpecification(nextStyle.terrain)
    await styleLoad.promise
    await completeMapStyleRefresh(map, requestId)
    if (requestId === mapStyleRequestId) {
      currentMapStyleTargetKey = targetKey
    }
  } finally {
    if (requestId === mapStyleRequestId) {
      pendingMapStyleTargetKey = null
      isMapStyleReloading.value = false
    }
  }
}

const syncOfflineStyleForCurrentWorkspace = async () => {
  if (!isTauri() || !mapInstance.value || !mapLoaded.value) return
  await setResolvedMapStyle(mapStylePreference.value)
}

const getInitialMapStyle = async (): Promise<{
  style: Exclude<MapOptions['style'], undefined>
  targetKey: string
}> => {
  try {
    const targetKey = getResolvedMapStyleTargetKey(mapStylePreference.value)
    const initialStyle = await buildResolvedMapStyle(mapStylePreference.value)
    setActiveTerrainSpecification(initialStyle.terrain)
    return {
      style: initialStyle.style,
      targetKey,
    }
  } catch (error) {
    logService.warn(
      LOG_CATEGORIES.MAP,
      'Falling back to default map style due to invalid preference',
      {
        error,
        preference: mapStylePreference.value,
      },
    )
    mapStylePreference.value = { ...DEFAULT_MAP_STYLE_PREFERENCE }
    persistMapStylePreference(mapStylePreference.value)
    const targetKey = getResolvedMapStyleTargetKey(mapStylePreference.value)
    const fallbackStyle = await buildResolvedMapStyle(mapStylePreference.value)
    setActiveTerrainSpecification(fallbackStyle.terrain)
    return {
      style: fallbackStyle.style,
      targetKey,
    }
  }
}

const selectBuiltInMapStyle = (styleId: BuiltInMapStyleId) => {
  isCustomStyleOptionsVisible.value = false
  applyMapStylePreference({ styleId })
}

const toggleCustomStyleOptions = () => {
  const nextVisible = !isCustomStyleOptionsVisible.value
  isCustomStyleOptionsVisible.value = nextVisible

  if (nextVisible) {
    customStyleNameInput.value = ''
    customStyleUrlInput.value = ''
  }
}

const getStyleNameFromSource = (source: string) => {
  const trimmed = source.trim()
  if (!trimmed) {
    return ''
  }

  try {
    const url = new URL(trimmed)
    const lastSegment = url.pathname.split('/').filter(Boolean).pop() || ''
    return decodeURIComponent(lastSegment)
      .replace(/\.json$/i, '')
      .trim()
  } catch {
    const lastSegment = trimmed.split('/').filter(Boolean).pop() || trimmed
    return lastSegment.replace(/\.json$/i, '').trim()
  }
}

const loadCustomStyleFromUrl = async () => {
  const rawCustomStyleUrl = customStyleUrlInput.value.trim()
  if (!rawCustomStyleUrl) {
    showError('Please enter a custom style URL')
    return
  }

  let customStyleUrl: string
  try {
    customStyleUrl = normalizeHttpsStyleUrl(rawCustomStyleUrl) as string
  } catch (error) {
    showError(error instanceof Error ? error.message : 'Custom style URL must use HTTPS')
    return
  }

  try {
    const response = await fetch(customStyleUrl)
    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`)
    }

    const json = (await response.json()) as unknown
    const rawJson = JSON.stringify(json)
    parseCustomMapStyleJson(rawJson)

    customStyleJsonInput.value = rawJson
    customStyleFileName.value = null
    customStyleNameInput.value = getStyleNameFromSource(customStyleUrl)
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to load custom style from URL', {
      customStyleUrl,
      error,
    })
    showError(
      `Could not load style from URL: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const saveSessionCustomStyle = () => {
  const name = customStyleNameInput.value.trim()
  let customStyleUrl: string | null = null
  try {
    customStyleUrl = normalizeHttpsStyleUrl(customStyleUrlInput.value)
  } catch (error) {
    showError(error instanceof Error ? error.message : 'Custom style URL must use HTTPS')
    return
  }

  const customStyleJson = customStyleJsonInput.value.trim() || null

  if (!name) {
    showError('Enter a name for the custom style')
    return
  }

  if (!customStyleUrl && !customStyleJson) {
    showError('Provide a custom style URL or upload a JSON file before saving')
    return
  }

  if (customStyleJson) {
    try {
      parseCustomMapStyleJson(customStyleJson)
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Invalid custom map style JSON')
      return
    }
  }

  const existingIndex = sessionCustomStylesStore.findIndex(
    (entry) => entry.name.toLowerCase() === name.toLowerCase(),
  )

  const nextEntry: SessionCustomStyleEntry = {
    id:
      existingIndex >= 0 ? sessionCustomStylesStore[existingIndex].id : nextSessionCustomStyleId++,
    name,
    customStyleUrl,
    customStyleJson,
    fileName: customStyleFileName.value,
  }

  if (existingIndex >= 0) {
    sessionCustomStylesStore.splice(existingIndex, 1, nextEntry)
  } else {
    sessionCustomStylesStore.push(nextEntry)
  }

  sessionCustomStyles.value = [...sessionCustomStylesStore]
  isCustomStyleOptionsVisible.value = false
}

const useSessionCustomStyle = (styleId: number) => {
  const entry = sessionCustomStylesStore.find((candidate) => candidate.id === styleId)
  if (!entry) {
    return
  }

  customStyleNameInput.value = entry.name
  customStyleUrlInput.value = entry.customStyleUrl ?? ''
  customStyleJsonInput.value = entry.customStyleJson ?? ''
  customStyleFileName.value = entry.fileName

  applyMapStylePreference({
    styleId: 'custom',
    customStyleUrl: entry.customStyleUrl,
    customStyleJson: entry.customStyleJson,
  })
}

const handleMapStyleCardClick = (card: MapStyleCard) => {
  if (card.cardType === 'builtin' && card.builtInStyleId) {
    selectBuiltInMapStyle(card.builtInStyleId)
    return
  }

  if (card.cardType === 'session' && typeof card.sessionStyleId === 'number') {
    isCustomStyleOptionsVisible.value = false
    useSessionCustomStyle(card.sessionStyleId)
  }
}

const isSessionCustomStyleActive = (entry: SessionCustomStyleEntry) => {
  if (mapStylePreference.value.styleId !== 'custom') {
    return false
  }

  const activeUrl = mapStylePreference.value.customStyleUrl?.trim() || null
  const activeJson = mapStylePreference.value.customStyleJson?.trim() || null

  return activeUrl === entry.customStyleUrl && activeJson === entry.customStyleJson
}

const isStyleCardSelected = (card: MapStyleCard) => {
  if (card.cardType === 'builtin' && card.builtInStyleId) {
    return mapStylePreference.value.styleId === card.builtInStyleId
  }

  if (card.cardType === 'session' && typeof card.sessionStyleId === 'number') {
    const entry = sessionCustomStyles.value.find(
      (candidate) => candidate.id === card.sessionStyleId,
    )
    return !!entry && isSessionCustomStyleActive(entry)
  }

  return false
}

const openCustomStyleFilePicker = () => {
  customStyleFileInput.value?.click()
}

const handleCustomStyleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) {
    return
  }

  try {
    const fileContent = await file.text()
    parseCustomMapStyleJson(fileContent)

    customStyleFileName.value = file.name
    customStyleJsonInput.value = fileContent
    customStyleNameInput.value = file.name.replace(/\.json$/i, '').trim()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to load custom style JSON file', {
      fileName: file.name,
      error,
    })
    showError(
      `Invalid style JSON file: ${error instanceof Error ? error.message : 'Could not parse file'}`,
    )
  } finally {
    target.value = ''
  }
}

const initializeMap = async () => {
  if (!mapContainer.value) return

  // Check WebGL support before attempting to create the map
  if (!isWebGLSupported()) {
    showError(
      'WebGL is not supported or disabled in your browser. This map requires WebGL to function properly. Please enable WebGL in your browser settings or try a different browser.',
    )
    return
  }

  try {
    const initialStyleResult = isE2eMode()
      ? { style: E2E_MAP_STYLE, targetKey: null }
      : await getInitialMapStyle()
    const initialStyle = initialStyleResult.style
    if (!isE2eMode()) {
      currentMapStyleTargetKey = initialStyleResult.targetKey
    }
    if (!mapContainer.value) return

    const mapConfig: MapOptions = {
      container: mapContainer.value,
      style: initialStyle,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
      attributionControl: false, // Disable default attribution control
      trackResize: false, // Disable automatic resize tracking to prevent unwanted map adjustments
      maxTileCacheZoomLevels: isTauri() ? 32 : 8,
      cancelPendingTileRequestsWhileZooming: true,
      // Native webviews cannot set a `Referer` header from JS, but `tiles.route.camp` enforces a
      // referrer allowlist. Reroute tile requests through the Tauri `rctiles` URI scheme so the Rust
      // handler can attach `Referer: https://route.camp`. Web/dev send their referrer natively, so
      // this only runs inside the native app.
      transformRequest: isTauri()
        ? (url) => {
            const nativeUrl = toNativeTileUrl(url, isTauriAndroidMobile())
            return nativeUrl ? { url: nativeUrl } : undefined
          }
        : undefined,
      dragPan: {
        linearity: 0.2,
        easing: easeOutCubic,
        maxSpeed: 1200,
        deceleration: 1800,
      },
    }

    if (props.initialBounds) {
      mapConfig.bounds = props.initialBounds
      mapConfig.fitBoundsOptions = {
        duration: 0,
        linear: true,
        maxZoom: 14,
        padding: 0,
        essential: true,
      }
    } else {
      mapConfig.center = MAP_CENTER
      mapConfig.zoom = MAP_ZOOM
    }

    mapInstance.value = new maplibregl.Map(mapConfig)
    configureMapInteractionSmoothing(mapInstance.value)
    if (isTouchDevice()) {
      increaseMobileRotationDeadband(mapInstance.value)
    }

    // Keep touch gestures enabled so mobile can use pinch-zoom, pitch, and
    // rotation alongside the pitch/zoom-driven terrain activation.
    mapInstance.value.touchZoomRotate.enable()
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)

    // Check for WebGL-specific errors
    if (
      errorMessage.includes('webgl') ||
      errorMessage.includes('WebGL') ||
      errorMessage.includes('Failed to initialize WebGL')
    ) {
      showError(
        'WebGL is disabled or unavailable in your browser. This may be due to enterprise security policies or browser settings. Please contact your system administrator or try a different browser.',
      )
    } else {
      showError(`Failed to initialize map: ${errorMessage}`)
    }
    return
  }

  // Add native attribution control (hidden via CSS � replaced by custom bar below)
  mapInstance.value.addControl(
    new maplibregl.AttributionControl({
      compact: true,
    }),
    'bottom-left',
  )

  // Add scale control - keep this one as it's useful and doesn't interfere with our layout
  mapInstance.value.addControl(new maplibregl.ScaleControl(), 'bottom-right')

  // Add controls based on screen size
  if (isSmallScreen.value) {
    // Keep mobile controls near the lower edge so route selection affordances stay reachable.
    const mobileControls = new MobileMapControls(
      toggleMapStylePanel,
      () => uiStore.toggleImmersiveMapMode(),
      () => uiStore.toggleMapElementsHiddenByButton(),
    )
    mapStyleControlHost.value = mobileControls
    mobileControls.setDeviceHeading(currentDeviceHeading)
    immersiveControlHost.value = mobileControls
    elementsVisibilityControlHost.value = mobileControls
    mapContentControlHost.value = mobileControls
    mobileControls.setImmersiveActive(uiStore.isImmersiveMapMode)
    mobileControls.setElementsHidden(uiStore.areMapElementsForceHidden)
    // Map content (POI categories) is available everywhere — in a collection you can also add POIs,
    // elsewhere it's view-only (browse Water/Cafe… on the map). Always show the control.
    mobileControls.setMapContentVisible(true)
    mapInstance.value.addControl(mobileControls, 'bottom-right')
  } else {
    // Add our custom combined controls to bottom-right on larger screens
    const combinedControls = new CombinedMapControls(toggleMapStylePanel, () =>
      uiStore.toggleMapElementsHiddenByButton(),
    )
    mapStyleControlHost.value = combinedControls
    combinedControls.setDeviceHeading(currentDeviceHeading)
    elementsVisibilityControlHost.value = combinedControls
    mapContentControlHost.value = combinedControls
    combinedControls.setElementsHidden(uiStore.areMapElementsForceHidden)
    // Map content (POI categories) is available everywhere (view-only outside a collection).
    combinedControls.setMapContentVisible(true)
    mapInstance.value.addControl(combinedControls, 'bottom-right')
  }

  mapInstance.value.on('load', async () => {
    const map = mapInstance.value as maplibregl.Map

    mapLoaded.value = true
    mapStyleRevision.value += 1

    await applyRoutecampContoursWithRetry(map)
    if (map !== mapInstance.value) {
      return
    }

    mapStore.setMapInstance(map)
    updateLiveAttribution()

    createDirectionArrow()

    void setupPassiveLocation()
    void restoreFollowLocationOnLaunch()

    emit('map-loaded', map)

    // Re-apply the chosen style once the map is ready so Tauri can switch
    // between online and offline-backed sources for the current workspace.
    if (isTauri()) {
      await syncOfflineStyleForCurrentWorkspace()
    }

    // Ensure canvas matches final layout once style is fully ready
    requestAnimationFrame(() => resizeMap())
  })

  mapInstance.value.on('error', (e: { error: Error }) => {
    const error = e.error
    // Transient render noise (tile fetch failures, cancelled requests, per-tile expression
    // mismatches) recovers on its own and would flood the Sync status log console on every
    // pan with a flaky connection — console-only warnings, no in-app log, no Sentry.
    if (error?.message?.includes('Expected value to be of type')) {
      logService.warn(
        LOG_CATEGORIES.MAP,
        'MapLibre tile expression type mismatch',
        { error },
        { consoleOnly: true },
      )
      return
    }
    if (error instanceof AJAXError) {
      logService.warn(
        LOG_CATEGORIES.MAP,
        `MapLibre resource request failed (HTTP ${error.status}): ${error.url}`,
        { error },
        { consoleOnly: true },
      )
      return
    }
    if (
      error?.name === 'AbortError' ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('NetworkError')
    ) {
      logService.warn(
        LOG_CATEGORIES.MAP,
        `MapLibre request failed: ${error.message}`,
        { error },
        { consoleOnly: true },
      )
      return
    }
    logService.error(LOG_CATEGORIES.MAP, `MapLibre error: ${error?.message ?? 'unknown'}`, {
      error,
    })
  })
  mapInstance.value.on('styledata', updateLiveAttribution)
  mapInstance.value.on('sourcedata', updateLiveAttribution)
  mapInstance.value.on('terrain', updateLiveAttribution)

  // Handle WebGL context loss to suppress browser warnings and attempt recovery
  const canvas = mapInstance.value.getCanvas()
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault()
    logService.warn(LOG_CATEGORIES.MAP, 'WebGL context lost')
  })
  canvas.addEventListener('webglcontextrestored', () => {
    logService.info(LOG_CATEGORIES.MAP, 'WebGL context restored')
  })

  mapInstance.value.on('click', (e: MapMouseEvent) => {
    if (isMapStylePanelOpen.value) {
      closeMapStylePanel()
    }
    emit('map-click', e)
  })
  mapInstance.value.on('dblclick', (e: MapMouseEvent) => {
    if (!mapInstance.value || !mapStyleControlHost.value?.isFollowLocationActive()) {
      return
    }

    e.preventDefault()
    mapInstance.value.easeTo({
      center: mapInstance.value.getCenter(),
      zoom: mapInstance.value.getZoom() + (e.originalEvent.shiftKey ? -1 : 1),
      duration: 300,
    })
  })

  // Add handler for map move events to emit center and zoom
  // Track whether the current movement was initiated by the user
  let userGestureInProgress = false
  let userGestureKinds = new Set<'pan' | 'zoom' | 'rotate' | 'pitch'>()
  const markUserGesture = (kind: 'pan' | 'zoom' | 'rotate' | 'pitch', e: unknown) => {
    const evt = e as { originalEvent?: Event } | undefined
    if (evt && evt.originalEvent) {
      if (!userGestureInProgress) {
        emit('map-interaction-start')
      }
      userGestureInProgress = true
      userGestureKinds.add(kind)
    }
  }
  const handleDragStart = (e: unknown) => {
    markUserGesture('pan', e)
    emit('map-pan-start')
  }
  mapInstance.value.on('dragstart', handleDragStart)
  mapInstance.value.on('zoomstart', (e: unknown) => markUserGesture('zoom', e))
  mapInstance.value.on('rotatestart', (e: unknown) => markUserGesture('rotate', e))
  mapInstance.value.on('pitchstart', (e: unknown) => markUserGesture('pitch', e))
  mapInstance.value.on('movestart', holdContourTilesForCameraMove)
  mapInstance.value.on('moveend', () => {
    if (mapInstance.value) {
      releaseContourTilesAfterBasemapSettles(mapInstance.value)
    }
  })
  mapInstance.value.on('moveend', () => {
    if (mapInstance.value) {
      if (refreshTileSourcesAfterNextMove) {
        refreshTileSourcesAfterNextMove = false
        refreshAllTileSources()
      }
      syncPitchZoomTerrain(mapInstance.value)
      const center = mapInstance.value.getCenter().toArray() as [number, number]
      const zoom = mapInstance.value.getZoom()
      const pitch = mapInstance.value.getPitch()
      const bearing = mapInstance.value.getBearing()
      const userInitiated = userGestureInProgress
      const gestureKinds = userGestureKinds
      userGestureInProgress = false
      userGestureKinds = new Set()
      emit('map-move', { center, zoom, pitch, bearing, userInitiated })
      // Notify app that user interacted with the map so UI shouldn't auto-adapt viewport
      if (userInitiated && !props.selectionMode) {
        window.dispatchEvent(
          new CustomEvent('user-map-interaction', { detail: { center, zoom, pitch, bearing } }),
        )
      }

      if (
        !props.selectionMode &&
        gestureKinds.has('pan') &&
        mapStyleControlHost.value?.isFollowLocationActive()
      ) {
        void mapStyleControlHost.value.stopFollowLocationIfPannedAway(
          FOLLOW_LOCATION_PAN_DEADBAND_PX,
        )
      }
    }
  })
  mapInstance.value.on('zoomend', () => {
    if (mapInstance.value) {
      syncPitchZoomTerrain(mapInstance.value)
    }
  })
  mapInstance.value.on('pitchend', () => {
    if (mapInstance.value) {
      syncPitchZoomTerrain(mapInstance.value)
    }
  })
}

const zoomToBounds = (bounds: maplibregl.LngLatBounds, options?: maplibregl.FitBoundsOptions) => {
  if (!mapInstance.value || !mapLoaded.value) return

  if (bounds && !bounds.isEmpty()) {
    const map = mapInstance.value
    const currentPadding = map.getPadding()
    const hasRetainedPadding = Object.values(currentPadding).some((value) => value !== 0)
    if (hasRetainedPadding) {
      map.setPadding({ top: 0, right: 0, bottom: 0, left: 0 })
    }
    const defaultOptions: maplibregl.FitBoundsOptions = {
      padding: 80,
      maxZoom: 14,
      duration: MAP_FIT_BOUNDS_DURATION_MS,
      linear: false,
      essential: true,
    }
    const nextOptions: maplibregl.FitBoundsOptions = { ...defaultOptions, ...options }
    map.fitBounds(bounds, optimizeFitBoundsCameraOptions(map, bounds, nextOptions))
  }
}

const flyTo = (center: [number, number], options?: maplibregl.FlyToOptions) => {
  if (!mapInstance.value || !mapLoaded.value) return

  const map = mapInstance.value
  const targetZoom = options?.zoom ?? 16
  const defaultOptions: maplibregl.FlyToOptions = isTauri()
    ? {
        zoom: 16,
        speed: 1.05,
        minZoom: Math.max(5, Math.min(map.getZoom(), targetZoom) - 4),
        essential: true,
      }
    : {
        zoom: 16,
        speed: 1.35,
        curve: 1.25,
        essential: true,
      }
  const nextOptions: maplibregl.FlyToOptions = { center, ...defaultOptions, ...options }
  map.flyTo(optimizeFlyToCameraOptions(map, nextOptions))
}

const isCollectionRouteOpen = () =>
  /\/(collections|workspaces)\/[^/]+/.test(window.location.pathname)

// Instantly restore a previously saved map viewport (no animation), so the
// collection opens exactly where the user left it.
const restoreViewport = (viewport: MapViewportSnapshot) => {
  if (!mapInstance.value || !mapLoaded.value) return
  mapInstance.value.jumpTo({
    center: viewport.center,
    zoom: viewport.zoom,
    pitch: viewport.pitch,
    bearing: viewport.bearing,
  })
}

// Follow mode owns the center, but a cold launch should still restore the user's
// chosen scale and orientation before the fresh location fix pans the camera.
const restoreViewportWithoutCenter = (viewport: MapViewportSnapshot) => {
  if (!mapInstance.value || !mapLoaded.value) return
  mapInstance.value.jumpTo({
    zoom: viewport.zoom,
    pitch: viewport.pitch,
    bearing: viewport.bearing,
  })
}

const getViewportSnapshot = (): MapViewportSnapshot | null => {
  if (!mapInstance.value || !mapLoaded.value) return null
  return {
    center: mapInstance.value.getCenter().toArray() as [number, number],
    zoom: mapInstance.value.getZoom(),
    pitch: mapInstance.value.getPitch(),
    bearing: mapInstance.value.getBearing(),
  }
}

// Persisted follow-location mode (Tauri): remember whether the locate control
// was tracking so app resume / relaunch can restore the mode. Resume itself
// never moves the camera — only a restored follow mode (or an active
// recording) recenters.
const persistFollowLocationState = (active: boolean) => {
  if (!isTauri()) return
  uiStatePersistenceService.setFollowLocationActive(active)
}

const refreshPassiveLocationAfterResume = async (
  source: string,
  options: { centerMap?: boolean } = {},
): Promise<void> => {
  if (!isTauri() || !mapInstance.value || !mapLoaded.value || !isCollectionRouteOpen()) return

  passivePermissionState = await getGeolocationPermissionState()
  if (passivePermissionState !== 'granted') {
    logService.info(LOG_CATEGORIES.MAP, 'Skipping resume location refresh', {
      source,
      permissionState: passivePermissionState,
    })
    return
  }

  try {
    const position = await getCurrentPosition({
      enableHighAccuracy: false,
      maximumAge: 0,
      timeout: 10000,
    })
    updatePassiveLocationMarker(position)

    const shouldCenterMap = options.centerMap === true
    if (shouldCenterMap && mapInstance.value) {
      // Recording exception: recenter on the fresh fix but keep the current zoom.
      mapInstance.value.easeTo({
        center: { lng: position.coords.longitude, lat: position.coords.latitude },
        duration: 800,
      })
    }

    logService.info(LOG_CATEGORIES.MAP, 'Refreshed passive location after resume', {
      source,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      centeredMap: shouldCenterMap,
    })

    if (!passiveLocationWatch) {
      void startPassiveLocationWatch()
    }
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to refresh passive location after resume', {
      source,
      error,
    })
  }
}

const isFollowLocationActive = () => mapStyleControlHost.value?.isFollowLocationActive() === true

const shouldRestoreFollowLocation = () =>
  isFollowLocationActive() || (isTauri() && uiStatePersistenceService.isFollowLocationActive())

const restoreFollowLocationAfterForeground = async (source: string): Promise<void> => {
  if (!mapInstance.value || !mapLoaded.value) return

  const runtimeFollowActive = mapStyleControlHost.value?.isFollowLocationActive() === true
  const permissionState = await getGeolocationPermissionState()
  if (permissionState === 'denied' || (isTauri() && permissionState !== 'granted')) {
    await mapStyleControlHost.value?.stopFollowLocation()
    // Permission was revoked while suspended — follow mode can no longer work.
    // Drop the persisted flag instead of surfacing an error on every resume.
    persistFollowLocationState(false)
    logService.info(LOG_CATEGORIES.MAP, 'Skipping follow-location restore', {
      source,
      permissionState,
    })
    return
  }

  // A persisted native mode must never prompt during launch/resume. An already-running
  // web follow session may continue when the Permissions API cannot report a state.
  if (permissionState !== 'granted' && !runtimeFollowActive) {
    persistFollowLocationState(false)
    logService.info(LOG_CATEGORIES.MAP, 'Skipping follow-location restore', {
      source,
      permissionState,
    })
    return
  }

  if (!shouldRestoreFollowLocation()) return

  logService.info(LOG_CATEGORIES.MAP, 'Restoring follow-location mode', { source })
  await mapStyleControlHost.value?.focusCurrentLocation({
    preserveZoom: true,
    allowStart: isTauri() && uiStatePersistenceService.isFollowLocationActive(),
  })
}

let foregroundFollowRestorePromise: Promise<void> | null = null
let lastForegroundFollowRestoreAt = 0

const requestForegroundFollowRestore = (source: string): Promise<void> => {
  if (!mapInstance.value || !mapLoaded.value) return Promise.resolve()
  if (foregroundFollowRestorePromise) return foregroundFollowRestorePromise

  const now = Date.now()
  if (now - lastForegroundFollowRestoreAt < FOLLOW_LOCATION_FOREGROUND_COALESCE_MS) {
    return Promise.resolve()
  }
  lastForegroundFollowRestoreAt = now

  const request = restoreFollowLocationAfterForeground(source).finally(() => {
    if (foregroundFollowRestorePromise === request) {
      foregroundFollowRestorePromise = null
    }
  })
  foregroundFollowRestorePromise = request
  return request
}

// iOS/Android may kill the webview while backgrounded, so a "resume" often
// arrives as a fresh launch instead of tauri://resumed. Restore a persisted
// follow mode on map load too, once per app start.
let hasRestoredFollowLocationOnLaunch = false

const restoreFollowLocationOnLaunch = async (): Promise<void> => {
  if (hasRestoredFollowLocationOnLaunch) return
  if (!isTauri()) return
  if (!uiStatePersistenceService.isFollowLocationActive()) return

  hasRestoredFollowLocationOnLaunch = true
  await requestForegroundFollowRestore('cold-start')
}

const handleTauriResume = () => {
  if (!isTauri()) return
  resizeMap()

  // Restored follow mode is the only user intent that moves the camera on
  // resume; otherwise only the passive marker refreshes.
  if (uiStatePersistenceService.isFollowLocationActive()) {
    void requestForegroundFollowRestore('tauri-resume')
    return
  }

  if (!isCollectionRouteOpen()) return

  // Active recording: the one context where resume recenters without follow
  // mode being on (matches Komoot/Gaia expectations).
  void refreshPassiveLocationAfterResume('tauri-resume', {
    centerMap: recordingStore.isActive,
  })
}

const setupTauriResumeListener = async () => {
  if (!isTauri() || isSettingUpTauriResumeListener || unlistenTauriResume) return

  isSettingUpTauriResumeListener = true
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window')
    const unlisten = await getCurrentWindow().listen(TAURI_WINDOW_RESUMED_EVENT, () => {
      handleTauriResume()
    })
    if (isTauriResumeListenerCancelled) {
      unlisten()
      return
    }
    unlistenTauriResume = unlisten
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to listen for Tauri resume events', { error })
  } finally {
    isSettingUpTauriResumeListener = false
  }
}

let resizePending = false

const resizeMap = () => {
  if (!mapInstance.value || !mapLoaded.value) return
  // Skip resize while suppressed (e.g. during drag-resize of library panel)
  if (uiStore.mapResizeSuppressed) {
    resizePending = true
    return
  }
  resizePending = false
  // Perform a robust resize: once now and again on the next frame to catch
  // layout that settles after CSS transitions/scrollbar changes.
  mapInstance.value.resize()
  requestAnimationFrame(() => {
    if (mapInstance.value && mapLoaded.value) {
      mapInstance.value.resize()
    }
  })
}

// When suppression ends, flush the pending resize
watch(
  () => uiStore.mapResizeSuppressed,
  (suppressed) => {
    if (!suppressed && resizePending) {
      resizeMap()
    }
  },
)

watch(
  () => uiStore.isImmersiveMapMode,
  (active) => immersiveControlHost.value?.setImmersiveActive(active),
)

watch(
  () => uiStore.areMapElementsForceHidden,
  (hidden) => elementsVisibilityControlHost.value?.setElementsHidden(hidden),
)

watch(isMapStylePanelOpen, (isOpen) => {
  mapStyleControlHost.value?.setMapStyleActive(isOpen)
})

// Tiles that fail while connectivity drops (or is absent) stay in MapLibre's errored state and
// are only re-requested on camera changes — leaving blank areas the offline cache could serve
// until the user pans or zooms. Re-sync package-backed sources and reload tiles whenever the
// active collection, package index, or effective connectivity changes.
let refreshTileSourcesAfterNextMove = false

const refreshAllTileSources = () => {
  const map = mapInstance.value
  if (!map) return

  const sources = map.getStyle()?.sources ?? {}
  for (const [sourceId, source] of Object.entries(sources)) {
    if (source.type !== 'vector' && source.type !== 'raster' && source.type !== 'raster-dem') {
      continue
    }
    try {
      map.refreshTiles(sourceId)
    } catch (error) {
      logService.warn(
        LOG_CATEGORIES.MAP,
        'Failed to refresh map tile source',
        { sourceId, error },
        { consoleOnly: true },
      )
    }
  }
}

const refreshTileSourcesForContextChange = () => {
  refreshTileSourcesAfterNextMove = true
  refreshAllTileSources()
}

watch(
  () => [
    mapLoaded.value,
    workspaceStore.clientId,
    connectivityService.isEffectivelyOnline.value,
    nativeTileProxyService.policyInitialized.value,
    nativeTileProxyService.policySynchronized.value,
    nativeTileProxyService.networkAllowed.value,
    getOfflineMapIndexKey(),
  ],
  async () => {
    if (
      isTauri() &&
      (!nativeTileProxyService.policyInitialized.value ||
        !nativeTileProxyService.policySynchronized.value ||
        nativeTileProxyService.networkAllowed.value !==
          connectivityService.isEffectivelyOnline.value)
    ) {
      return
    }
    await syncOfflineStyleForCurrentWorkspace()
    refreshTileSourcesForContextChange()
  },
)

// Create or move the passive user position circle to the given coordinates.
const updatePassiveLocationMarker = (position: GeoPosition): void => {
  if (!mapInstance.value) return

  const lngLat = { lng: position.coords.longitude, lat: position.coords.latitude }
  passiveGpsHeading = normalizeLocationHeading(position.coords.heading)
  emitCurrentLocation(position, 'passive')

  if (!passiveLocationMarker) {
    passiveLocationMarker = createMapMarker({
      element: createUserLocationMarkerElement('passive'),
      rotationAlignment: 'map',
      pitchAlignment: 'viewport',
    })
      .setLngLat(lngLat)
      .addTo(mapInstance.value)
  } else {
    passiveLocationMarker.setLngLat(lngLat)
  }
  updateUserLocationMarkerHeading(passiveLocationMarker, currentDeviceHeading ?? passiveGpsHeading)

  passiveLocationMarker.getElement().style.display = suppressPassiveLocationMarker ? 'none' : ''
}

// Hide/show the passive circle so it never overlaps the active tracking marker.
const setPassiveLocationMarkerSuppressed = (suppressed: boolean): void => {
  suppressPassiveLocationMarker = suppressed
  if (passiveLocationMarker) {
    passiveLocationMarker.getElement().style.display = suppressed ? 'none' : ''
  }
}

const removePassiveLocationMarker = (): void => {
  if (passiveLocationMarker) {
    passiveLocationMarker.remove()
    passiveLocationMarker = null
  }
  passiveGpsHeading = null
}

// Start watching the user's location passively (no recentering, no prompt).
const startPassiveLocationWatch = async (): Promise<void> => {
  if (passiveLocationWatch || !isGeolocationSupported()) return

  try {
    // A passive position dot does not need GPS precision, so use a low-power,
    // cache-friendly configuration. This resolves quickly from Wi-Fi/network
    // location and avoids the timeouts high-accuracy GPS hits on desktop.
    passiveLocationWatch = await watchPosition(
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 60000 },
      (position) => updatePassiveLocationMarker(position),
      (error) => {
        // Timeouts are expected/transient; watchPosition keeps retrying, so just
        // log and never surface a user-facing error for the passive indicator.
        logService.warn(LOG_CATEGORIES.MAP, 'Passive location watch error', { error })
      },
    )
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to start passive location watch', { error })
  }
}

const stopPassiveLocationWatch = (): void => {
  if (passiveLocationWatch) {
    void clearWatch(passiveLocationWatch)
    passiveLocationWatch = null
  }
  removePassiveLocationMarker()
}

// Show the passive location circle only when geolocation permission is already
// granted, and react to later permission changes (e.g. via the locate button)
// so we never trigger an unsolicited permission prompt on map load.
const setupPassiveLocation = async (): Promise<void> => {
  if (!isGeolocationSupported()) return

  if (isTauri()) {
    passivePermissionState = await getGeolocationPermissionState()
    if (passivePermissionState === 'granted') {
      void startPassiveLocationWatch()
    }
    return
  }

  // Without the Permissions API we cannot tell whether access is granted without
  // prompting, so stay passive and rely on the locate button instead.
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return

  try {
    passivePermissionStatus = await navigator.permissions.query({ name: 'geolocation' })

    const applyPermissionState = () => {
      passivePermissionState = passivePermissionStatus?.state ?? 'unknown'
      if (passivePermissionStatus?.state === 'granted') {
        void startPassiveLocationWatch()
      } else {
        stopPassiveLocationWatch()
      }
    }

    passivePermissionChangeHandler = applyPermissionState
    passivePermissionStatus.addEventListener('change', applyPermissionState)
    applyPermissionState()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to query geolocation permission', { error })
  }
}

const cleanupPassiveLocation = (): void => {
  if (passivePermissionStatus && passivePermissionChangeHandler) {
    passivePermissionStatus.removeEventListener('change', passivePermissionChangeHandler)
  }
  passivePermissionStatus = null
  passivePermissionState = null
  passivePermissionChangeHandler = null
  stopPassiveLocationWatch()
}

// Give basemap tiles network/worker priority during camera movement: contour
// tile generation pauses from movestart until the basemap has settled after
// moveend (or a timeout, so contours never stall behind a source that keeps
// loading, e.g. on very slow connections).
const CONTOUR_RELEASE_TIMEOUT_MS = 3000
let releaseContourTileHold: (() => void) | null = null
let cancelContourSettleWatch: (() => void) | null = null

const holdContourTilesForCameraMove = () => {
  cancelContourSettleWatch?.()
  releaseContourTileHold ??= holdRoutecampContourTiles()
}

const releaseContourTileHoldNow = () => {
  cancelContourSettleWatch?.()
  releaseContourTileHold?.()
  releaseContourTileHold = null
}

const releaseContourTilesAfterBasemapSettles = (map: maplibregl.Map) => {
  cancelContourSettleWatch?.()
  if (!releaseContourTileHold) return

  if (areRoutecampBasemapSourcesLoaded(map)) {
    releaseContourTileHoldNow()
    return
  }

  const checkSettled = () => {
    if (areRoutecampBasemapSourcesLoaded(map)) {
      releaseContourTileHoldNow()
    }
  }
  const timeoutId = window.setTimeout(releaseContourTileHoldNow, CONTOUR_RELEASE_TIMEOUT_MS)
  map.on('sourcedata', checkSettled)
  cancelContourSettleWatch = () => {
    cancelContourSettleWatch = null
    window.clearTimeout(timeoutId)
    map.off('sourcedata', checkSettled)
  }
}

const cleanupMapResources = () => {
  cleanupPassiveLocation()
  setActiveTerrainSpecification(null)
  releaseContourTileHoldNow()

  if (!mapInstance.value) return

  restoreExpensiveTerrainLayers(mapInstance.value)
  mapInstance.value.off('styledata', updateLiveAttribution)
  mapInstance.value.off('sourcedata', updateLiveAttribution)
  mapInstance.value.off('terrain', updateLiveAttribution)
  mapInstance.value.remove()
  mapInstance.value = null
  liveAttributionHtml.value = ''
}

// Debounced window resize handler to keep map canvas in sync with container size
let resizeRaf: number | null = null
const onWindowResize = () => {
  if (resizeRaf) cancelAnimationFrame(resizeRaf)
  resizeRaf = requestAnimationFrame(() => resizeMap())
}

// Use Vue composable for window resize events
useEventListener(window, 'resize', onWindowResize)
useEventListener(window, 'orientationchange', onWindowResize)
window.visualViewport?.addEventListener('resize', onWindowResize)
useEventListener(document, 'visibilitychange', () => {
  if (document.visibilityState !== 'visible') return

  onWindowResize()
  if (shouldRestoreFollowLocation()) {
    void requestForegroundFollowRestore('visibility-visible')
    return
  }
  void refreshPassiveLocationAfterResume('visibility-visible')
})
useEventListener(window, 'focus', () => {
  if (!shouldRestoreFollowLocation()) return
  onWindowResize()
  void requestForegroundFollowRestore('window-focus')
})
useEventListener(document, 'fullscreenchange', onWindowResize)
useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isMapStylePanelOpen.value) {
    closeMapStylePanel()
  }
})

onMounted(() => {
  unsubscribeDeviceHeading = deviceHeadingService.subscribe((heading) => {
    currentDeviceHeading = heading
    mapStyleControlHost.value?.setDeviceHeading(heading)
    if (passiveLocationMarker) {
      updateUserLocationMarkerHeading(passiveLocationMarker, heading ?? passiveGpsHeading)
    }
  })
  void setupTauriResumeListener()
  let cleanupBottomEdgePanGuard: (() => void) | null = null

  if (mapContainer.value) {
    void initializeMap().then(() => {
      if (!mapContainer.value || !mapInstance.value || !isTauriMobile() || !isSmallScreen.value) {
        return
      }

      cleanupBottomEdgePanGuard = installBottomEdgePanGuard(mapContainer.value, mapInstance.value)
    })
  }

  const cleanupPinchClickGuard = mapContainer.value
    ? installPinchClickGuard(mapContainer.value)
    : null

  // Observe container resize (covers sidebars/panels affecting layout)
  let ro: ResizeObserver | null = null
  if ('ResizeObserver' in window) {
    ro = new ResizeObserver(() => onWindowResize())
    // Observe both the map element and its parent container to catch layout changes
    if (mapContainer.value) ro.observe(mapContainer.value)
    if (mapContainer.value?.parentElement) ro.observe(mapContainer.value.parentElement)
    if (rootEl.value) ro.observe(rootEl.value)
  }

  // Also react at the end of CSS transitions that affect size
  const transitionHandler = (e: TransitionEvent) => {
    const prop = e.propertyName
    if (!prop) return
    if (prop.includes('width') || prop.includes('height') || prop.includes('inset')) {
      onWindowResize()
    }
  }
  rootEl.value?.addEventListener('transitionend', transitionHandler)
  mapContainer.value?.parentElement?.addEventListener('transitionend', transitionHandler)

  // Cleanup for ResizeObserver when component unmounts
  onUnmounted(() => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf)
    window.visualViewport?.removeEventListener('resize', onWindowResize)
    if (ro) ro.disconnect()
    cleanupPinchClickGuard?.()
    cleanupBottomEdgePanGuard?.()
    rootEl.value?.removeEventListener('transitionend', transitionHandler)
    mapContainer.value?.parentElement?.removeEventListener('transitionend', transitionHandler)
  })
})

onUnmounted(() => {
  unsubscribeDeviceHeading?.()
  unsubscribeDeviceHeading = null
  currentDeviceHeading = null
  isTauriResumeListenerCancelled = true
  unlistenTauriResume?.()
  unlistenTauriResume = null
  cleanupMapResources()
})

defineExpose({
  zoomToBounds,
  flyTo,
  resizeMap,
  restoreViewport,
  restoreViewportWithoutCenter,
  getViewportSnapshot,
  isFollowLocationActive,
})
</script>

<template>
  <div ref="rootEl" class="relative w-full h-full">
    <div
      ref="mapContainer"
      :class="[
        'absolute inset-0 w-full h-full touch-none',
        isMapStyleReloading ? 'pointer-events-none' : '',
      ]"
    ></div>
    <input
      ref="customStyleFileInput"
      type="file"
      accept=".json,application/json"
      class="hidden"
      @change="handleCustomStyleFileChange"
    />
    <!-- Desktop: docked floating style panel. Mobile uses the MapToolSheet below. -->
    <div
      v-if="isMapStylePanelOpen && !isSmallScreen"
      class="map-style-panel"
      :class="{
        'map-style-panel-custom-open': isCustomStyleOptionsVisible,
      }"
    >
      <div v-if="isCustomStyleOptionsVisible" class="map-style-custom-section">
        <label class="map-style-label" for="custom-style-url">Custom style source</label>
        <div class="map-style-inline-row">
          <input
            id="custom-style-url"
            v-model="customStyleUrlInput"
            type="url"
            class="map-style-input"
            placeholder="https://example.com/style.json"
          />
          <button type="button" class="map-style-action-btn" @click="loadCustomStyleFromUrl">
            Load
          </button>
        </div>
        <button
          type="button"
          class="map-style-action-btn map-style-upload-btn"
          @click="openCustomStyleFilePicker"
        >
          Upload .json
        </button>
        <p v-if="customStyleFileName" class="map-style-file-name">
          Loaded: {{ customStyleFileName }}
        </p>

        <div class="map-style-save-row">
          <label class="map-style-label" for="custom-style-name">Save Style (Session)</label>
          <div class="map-style-inline-row">
            <input
              id="custom-style-name"
              v-model="customStyleNameInput"
              type="text"
              class="map-style-input"
              placeholder="My Test Style"
            />
            <button type="button" class="map-style-action-btn" @click="saveSessionCustomStyle">
              Save
            </button>
          </div>
        </div>
      </div>

      <div class="map-style-panel-title">Map type</div>
      <MapStyleTiles
        :cards="mapStyleCards"
        :is-selected="isStyleCardSelected"
        :custom-open="isCustomStyleOptionsVisible"
        @select="handleMapStyleCardClick"
        @toggle-custom="toggleCustomStyleOptions"
      />
    </div>

    <!-- Mobile: map type as a non-modal draggable sheet with large preview tiles, so the basemap
         change is visible on the live map behind it (Apple/Google pattern). -->
    <MapToolSheet
      v-if="isSmallScreen"
      :open="isMapStylePanelOpen"
      title="Map type"
      :icon="MapIcon"
      initial-mode="peek"
      testid="map-style-sheet"
      @close="closeMapStylePanel"
    >
      <MapStyleTiles
        class="px-4 pb-3 pt-1"
        :cards="mapStyleCards"
        :is-selected="isStyleCardSelected"
        :custom-open="isCustomStyleOptionsVisible"
        @select="handleMapStyleCardClick"
        @toggle-custom="toggleCustomStyleOptions"
      />

      <!-- Custom style controls (collapsible), reusing the desktop inputs/handlers. -->
      <div
        v-if="isCustomStyleOptionsVisible"
        class="space-y-2 border-t border-gray-100 px-4 py-3 text-sm"
      >
        <label class="text-xs font-medium text-gray-600" for="custom-style-url-mobile"
          >Custom style source</label
        >
        <div class="flex gap-2">
          <input
            id="custom-style-url-mobile"
            v-model="customStyleUrlInput"
            type="url"
            class="min-w-0 flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            placeholder="https://example.com/style.json"
          />
          <button
            type="button"
            class="shrink-0 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:cursor-pointer"
            @click="loadCustomStyleFromUrl"
          >
            Load
          </button>
        </div>
        <button
          type="button"
          class="w-full rounded-lg border border-gray-300 py-1.5 text-sm font-medium text-gray-700 hover:cursor-pointer"
          @click="openCustomStyleFilePicker"
        >
          Upload .json
        </button>
        <p v-if="customStyleFileName" class="text-xs text-gray-500">
          Loaded: {{ customStyleFileName }}
        </p>
      </div>
    </MapToolSheet>

    <!-- Custom attribution bar -->
    <div
      class="absolute bottom-0 right-0 hidden sm:flex items-center gap-0.5 text-[11px] text-stone-800 bg-white/60 px-2 py-0.5 pointer-events-auto"
      :style="{ zIndex: 14 }"
    >
      <span
        v-if="liveAttributionHtml"
        class="map-attribution-content"
        v-html="liveAttributionHtml"
      ></span>
      <span v-if="liveAttributionHtml">|</span>
      <button
        class="hover:text-stone-600 transition-colors cursor-pointer"
        @click="openStaticPage('about')"
      >
        About
      </button>
      <span>|</span>
      <button
        class="hover:text-stone-600 transition-colors cursor-pointer"
        @click="openStaticPage('eu-data-protection')"
      >
        Data Protection
      </button>
      <span>|</span>
      <button
        class="hover:text-stone-600 transition-colors cursor-pointer"
        @click="openStaticPage('privacy-policy')"
      >
        Privacy
      </button>
      <span>|</span>
      <button
        class="hover:text-stone-600 transition-colors cursor-pointer"
        @click="openStaticPage('imprint')"
      >
        Imprint
      </button>
    </div>
    <div v-if="mapLoaded">
      <slot></slot>
    </div>
  </div>
</template>

<style>
/* Hide native attribution control � replaced by custom bar */
.maplibregl-ctrl-attrib {
  display: none !important;
}

/*
 * Reassert full touch ownership on the map surface so MapLibre receives the
 * raw gesture stream and can switch zoom anchoring dynamically.
 */
.maplibregl-map,
.maplibregl-canvas-container,
.maplibregl-canvas {
  touch-action: none;
}

/* Fix control positioning - keep controls inside the map container */

.maplibregl-ctrl-bottom-right {
  right: 16px;
  bottom: 40px;
  transform: none;
  /* Layering: keep controls above map markers (12) but below toolbar (15) and dialogs */
  z-index: 14 !important;
  position: absolute !important;
  pointer-events: auto !important;
}

/* iOS specific fixes for map controls container */
@supports (-webkit-touch-callout: none) {
  .maplibregl-ctrl-bottom-right {
    position: absolute !important;
    /* Above markers (12), below toolbar (15) */
    z-index: 14 !important;
    bottom: calc(40px + env(safe-area-inset-bottom)) !important;
    right: calc(16px + env(safe-area-inset-right)) !important;
    /* NB: deliberately no transform/will-change here. A transform on this corner makes it the
       containing block for the position:fixed control cluster inside it — and since the corner's
       bottom is floated per-screen for the scale bar, that dragged the cluster to a different spot
       on every view. Without the transform the cluster's fixed offset resolves against the viewport
       (stable everywhere); the scale floats via its own margin instead (see the mobile rule). */
  }
}

/* Control container styling */
.circular-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  flex-direction: row; /* Ensure controls are arranged horizontally */
}

/* Zoom controls — joined into one rounded-full capsule (Apple Maps language) */
.zoom-controls-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
  background-color: white;
  border-radius: 9999px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.09);
  overflow: hidden;
}

/* Zoom buttons sit flush inside the capsule (no own shadow/radius); hairline divider between them */
.zoom-controls-wrapper .map-ctrl-btn,
.zoom-controls-wrapper .map-ctrl-btn button {
  background-color: transparent;
  border-radius: 0;
  box-shadow: none;
}
.zoom-controls-wrapper .map-ctrl-btn:first-child {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

/* Shared control button wrapper — round circle (separate floating controls) */
.map-ctrl-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 9999px;
  width: 40px;
  height: 40px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.09);
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  position: relative;
  z-index: 1;
}

/* Ensure Lucide icons are properly centered */
.map-ctrl-btn button div {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* Style for control buttons */
.map-ctrl-btn button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 9999px;
  /* iOS specific touch fixes */
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;
}

.map-ctrl-btn button:hover {
  background-color: #f5f5f5;
}

.maplibregl-ctrl-map-style.is-active {
  color: var(--color-primary-600);
}

/* Tracks hidden: tint the eye toggle so the "off" state reads at a glance. */
.maplibregl-ctrl-hide-elements.is-active {
  color: var(--color-primary-600);
}

.map-style-panel {
  position: absolute;
  right: 56px;
  bottom: 40px;
  z-index: 14;
  width: fit-content;
  min-width: 0;
  max-width: min(520px, calc(100vw - 140px));
  max-height: min(75vh, 460px);
  padding: 14px;
  border-radius: 16px;
  border: 1px solid rgba(28, 25, 23, 0.08);
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  box-shadow: 0 14px 30px rgba(28, 25, 23, 0.17);
  overflow: auto;
}

.map-style-panel-custom-open {
  min-width: min(320px, calc(100vw - 140px));
}

.map-style-panel-title {
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #111827;
}

.map-style-custom-section {
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(28, 25, 23, 0.12);
  padding-bottom: 10px;
}

.map-style-label {
  display: block;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
  color: #1c1917;
  margin-bottom: 4px;
}

.map-style-inline-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.map-style-input {
  width: 100%;
  border: 1px solid rgba(28, 25, 23, 0.2);
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #1c1917;
  background: #fff;
}

.map-style-action-btn {
  border: 1px solid rgba(28, 25, 23, 0.2);
  border-radius: 6px;
  background: #fafaf9;
  color: #1c1917;
  padding: 6px 8px;
  font-size: 12px;
  white-space: nowrap;
}

.map-style-action-btn:hover {
  background: #f5f5f4;
}

.map-style-upload-btn {
  width: 100%;
  margin-top: 6px;
}

.map-style-file-name {
  margin-top: 6px;
  font-size: 11px;
  color: #57534e;
}

.map-style-save-row {
  margin-top: 8px;
}

/* Hide the actual geolocate button that will be triggered programmatically */
.maplibregl-ctrl-geolocate.maplibregl-ctrl-group {
  display: none;
}

/* Scale bar lives in the bottom-RIGHT corner (clear of the bottom-left create FAB; matches Komoot). */
.maplibregl-ctrl-scale {
  margin: 0 10px 10px 0 !important;
}

/* On mobile, float the scale just above whatever occupies the bottom (sheet/drawer/tab bar) via
   `--map-scale-bottom` (set by WorkspaceMapContainer) so it stays in the visible map and is never
   hidden behind the sheet. The round control cluster is `position: fixed` and so is unaffected by
   moving the bottom-right corner container. */
@media (max-width: 767px) {
  /* Keep the corner pinned to the very bottom (never move it — it's the fixed cluster's containing
     block on some engines, so moving it would move the cluster). Float ONLY the scale element up
     above the bottom sheet via its own margin. */
  .maplibregl-ctrl-bottom-right {
    bottom: env(safe-area-inset-bottom, 0px) !important;
  }
  .maplibregl-ctrl-bottom-right .maplibregl-ctrl-scale {
    margin: 0 12px var(--map-scale-bottom, 80px) 0 !important;
  }
}

/* Compass icon rotation and active state */
.compass-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
  transform: rotate(-45deg); /* Base rotation: 45 degrees counter-clockwise */
}

/* The lucide Navigation arrow's filled mass sits above-and-right of its box centre, so flex-centring
   the box leaves the arrow looking high. Shift the glyph to put its visual centroid on the button
   centre — rotation-independent (the wrapper carries the dynamic bearing rotation), so it stays
   centred at every bearing. */
.compass-icon-wrapper svg {
  transform: translate(-1.4px, 1.4px);
}

.maplibregl-ctrl-compass.is-active {
  color: var(--color-primary-600);
}

/* Hide the default geolocate control but keep it functional */
.maplibregl-ctrl-top-left .maplibregl-ctrl-geolocate {
  position: absolute;
  visibility: hidden;
  top: -9999px;
  left: -9999px;
}

/* Active state for custom geolocate button */
.maplibregl-ctrl-geolocate.active {
  color: var(--color-primary-600);
  position: relative;
}

/* Geolocate button when tracking is active - shows a blue dot indicator */
.maplibregl-ctrl-geolocate.active::after {
  content: '';
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background-color: var(--color-primary-600);
  border-radius: 50%;
  box-shadow: 0 0 0 2px white;
}

/* Passive user position circle shown whenever the current location is available. */
.passive-user-location-marker {
  position: relative;
  width: 14px;
  height: 14px;
  pointer-events: none;
}

.passive-location-marker-inner {
  position: relative;
  z-index: 1;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: var(--color-primary-600);
  border: 2.5px solid #fff;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.35);
}

.location-marker-heading {
  position: absolute;
  z-index: 0;
  bottom: 50%;
  left: 50%;
  display: none;
  width: 30px;
  height: 34px;
  background-color: var(--color-primary-600);
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.8));
  opacity: 0.55;
  pointer-events: none;
  transform: translateX(-50%);
}

.location-marker-heading::after {
  content: '';
  position: absolute;
  inset: 2px;
  background-color: rgba(255, 255, 255, 0.65);
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
}

.has-heading .location-marker-heading {
  display: block;
}

/* Custom user location marker styling */
.custom-user-location-marker {
  position: relative;
  width: 16px;
  height: 16px;
  margin-top: -8px;
  margin-left: -8px;
  pointer-events: none;
}

.location-marker-inner {
  position: relative;
  z-index: 1;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--color-primary-600);
  border: 3px solid white;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.3);
}

.location-marker-pulse {
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(25, 118, 210, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: pulse 2s infinite;
  z-index: -1;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  70% {
    transform: scale(3);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

/* Combined map controls container */
.combined-map-controls {
  background-color: transparent;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
  width: auto !important;
  /* iOS specific fixes */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  position: relative;
  /* Above markers (12), below toolbar (15) */
  z-index: 14 !important;
  pointer-events: auto !important;
}

/* Aggressive iOS fixes for control visibility */
@supports (-webkit-touch-callout: none) {
  .combined-map-controls {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    will-change: transform;
  }

  .controls-container {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  /* Ensure all control wrappers are visible on iOS */
  .map-ctrl-btn {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
    /* Child wrappers can stay at 1; container controls overall stacking */
    z-index: 1 !important;
    pointer-events: auto !important;
  }

  /* Ensure all buttons are visible and clickable */
  .map-ctrl-btn button {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    pointer-events: auto !important;
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
  }
}

/* Controls container to arrange all controls vertically */
.controls-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

/* Mobile controls container to arrange controls vertically */
.mobile-controls-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

/* Mobile map controls positioning */
.mobile-map-controls {
  background-color: transparent;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
  width: auto !important;
}

/* Mobile control cluster: bottom-anchored on the right edge at a FIXED, stable offset (200px, well
   above the tab bar + a collapsed sheet's peek). It deliberately does NOT track any sheet's live
   height (true vertical-centring via `top: 50%` breaks here — `position: fixed` resolves against a
   transformed MapLibre ancestor, not the viewport, dropping the cluster low). `--mmc-opacity` /
   `--mmc-pe` (set by WorkspaceMapContainer) fade it out once a sheet expands past its peek, so the
   200px resting spot only ever needs to clear a peek. */
.maplibregl-ctrl-bottom-right .mobile-map-controls {
  right: 16px;
  bottom: 200px;
  transform: none;
  /* Above markers (12), below toolbar (15) */
  z-index: 14 !important;
  position: fixed !important;
  pointer-events: var(--mmc-pe, auto) !important;
  opacity: var(--mmc-opacity, 1);
  transition: opacity 160ms ease;
}

/* iOS specific fixes for mobile controls */
@supports (-webkit-touch-callout: none) {
  .maplibregl-ctrl-bottom-right .mobile-map-controls {
    position: fixed !important;
    /* Above markers (12), below toolbar (15) */
    z-index: 14 !important;
    bottom: calc(200px + env(safe-area-inset-bottom)) !important;
    right: calc(16px + env(safe-area-inset-right)) !important;
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    will-change: transform;
  }

  .mobile-controls-container {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  /* Ensure mobile control wrappers are visible on iOS */
  .mobile-map-controls .map-ctrl-btn {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
    z-index: 1 !important;
    pointer-events: var(--mmc-pe, auto) !important;
  }

  /* Ensure mobile control buttons are visible and clickable */
  .mobile-map-controls .map-ctrl-btn button {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    pointer-events: var(--mmc-pe, auto) !important;
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
  }
}
</style>
